import { AuthContext } from '../../../context/AuthContext'
import { getApiAuthToken } from '../../../services/api'
import { WorkerCredentialSummaryPanel } from './WorkerCredentialSummaryPanel'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTable } from '../../../hooks/useSmartTable'
import { TableQueryControls, TableDisplayControls } from '../../ui/SmartTableControls'
import { workerTableDefinition, type WorkerTableFilters } from './productivityTables'
import { AdminTableContainer } from './AdminUiPrimitives'
import { useListCommands } from '../../../hooks/useListCommands'
import {
  createWorkerDocumentForManager,
  createWorkerInviteCode,
  createWorkerOrgRelationship,
  endWorkerOrgRelationship,
  downloadWorkerDocumentForManager,
  expireWorkerDocument,
  getWorkerProfile,
  getWorkerAvailabilityForAdmin,
  getWorkerReliability,
  listWorkerAttendanceEventsForAdmin,
  listWorkerDocumentsForManager,
  listWorkerDocumentRisks,
  listWorkerInviteCodes,
  listWorkerReviewsForAdmin,
  listFreeAgents,
  listOrgWorkers,
  listSkills,
  listWorkerProfilesPage,
  revokeWorkerInviteCode,
  updateWorkerOrgRelationship,
  updateWorkerDocumentForManager,
  updateWorkerProfile,
  updateWorkerProfileVisibility,
  rejectWorkerDocument,
  removeWorkerDocumentForManager,
  replaceWorkerDocumentSkillLinks,
  revokeWorkerDocument,
  runSystemWorkerLifecycleAction,
  verifyWorkerDocument,
  apiErrorMessage,
} from '../../../services/access'
import type { WorkerInviteCode } from '../../../services/access'
import type {
  FreeAgentWorkerProfile,
  Organization,
  Skill,
  WorkerProfileDocument,
  WorkerDocumentRisk,
  WorkerOrgRelationship,
  WorkerProfileDetail,
  WorkerAvailabilitySummary,
  WorkerAttendanceEvent,
  WorkerReliabilityProfile,
  WorkerSupervisorReview,
  WorkerProfileVisibilityPayload,
  WorkerRelationshipPayload,
  WorkerRelationshipStatus,
  WorkerRelationshipType,
} from '../../../types/access'
import { formatDate, formatOrgType } from './adminPanelUtils'
import { WorkerDocumentConsentSection } from './WorkerDocumentConsentSection'
import { EmploymentCompliancePanel } from './EmploymentCompliancePanel'
import { AdminRowActionMenu, ConfirmDialog, DetailDrawer, EmptyState, ErrorBanner, FormModal, LoadingState, PaginationControls } from './AdminUiPrimitives'
import type { AdminRowAction } from './AdminUiPrimitives'
import { SkillPicker } from '../../ui/SkillPicker'
import { skillService, type SkillCatalogResult, type SkillRecommendation, type WorkerCanonicalSkill, type WorkerSkillEvidence, type WorkerSkillExperience, type WorkerSkillProgression } from '../../../services/skills'
import { useI18n } from '../../../i18n/I18nContext'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { useAdminExperience } from '../../../context/AdminExperienceContext'
import { useAccessContext } from '../../../context/AccessContext'
import { ShiftHistoryViewer } from './ShiftHistoryViewer'

interface WorkersPanelProps {
  organizations: Organization[]
  selectedOrgId: string
  canViewWorkers: boolean
  canManageWorkers: boolean
  canShareWorkers: boolean
  canManageDocumentConsent: boolean
  requiresExplicitOrgContext?: boolean
  initialSelectedWorkerProfileId?: string
  onClearInitialSelectedWorkerProfileId?: () => void
  onSelectOrg: (orgId: string) => void
}

type WorkerFilter = 'own' | 'company' | 'shared' | 'exclusive' | 'free-agents' | 'pending'
type DocumentRiskFilter = '' | 'missing' | 'expired' | 'expiring_soon'
type WorkerDetailTab = 'overview' | 'employment' | 'compliance' | 'skills' | 'availability' | 'activity'
type StaffSurface = 'staff' | 'relationships' | 'marketplace' | 'invites'

const workerFilters: Array<{ id: WorkerFilter; label: string }> = [
  { id: 'own', label: 'Own Workers' },
  { id: 'company', label: 'Company Employees' },
  { id: 'shared', label: 'Shared Workers' },
  { id: 'exclusive', label: 'Exclusive Workers' },
  { id: 'free-agents', label: 'Free Agents' },
  { id: 'pending', label: 'Pending/Inactive' },
]
const workerDetailTabs: Array<{ id: WorkerDetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'employment', label: 'Employment & Access' },
  { id: 'compliance', label: 'Compliance & Documents' },
  { id: 'skills', label: 'Skills' },
  { id: 'availability', label: 'Availability' },
  { id: 'activity', label: 'Activity' },
]
const staffSurfaces: Array<{ id: StaffSurface; label: string; description: string }> = [
  { id: 'staff', label: 'Own staff', description: 'Direct workers owned by the selected organization.' },
  { id: 'relationships', label: 'Relationship admin', description: 'Shared, exclusive, and inactive relationship maintenance.' },
  { id: 'marketplace', label: 'Network-visible workers', description: 'Free-agent visibility, not organization staff.' },
  { id: 'invites', label: 'Invites & visibility', description: 'Invite codes and worker marketplace settings.' },
]

const relationshipTypes: WorkerRelationshipType[] = ['agency_worker', 'company_employee', 'company_contractor', 'shared_worker', 'exclusive_worker']
const relationshipStatuses: WorkerRelationshipStatus[] = ['pending', 'active', 'suspended', 'ended']
const workerDocumentTypeOptions = [
  'profile_photo', 'certificate', 'license', 'training_card', 'safety_induction', 'safety_card',
  'government_id', 'iqama', 'passport', 'national_id', 'driver_license_id', 'work_permit',
  'site_access_card', 'medical_clearance', 'immigration_document', 'tax_document', 'background_check',
  'direct_deposit_form', 'resume', 'portfolio', 'trade_certificate', 'other',
]
const marketplaceVisibilityOptions: Array<NonNullable<WorkerProfileVisibilityPayload['marketplaceVisibility']>> = ['private', 'visible_to_selected', 'visible_to_all']
const workerLifecycleActionLabels: Record<'suspend' | 'archive' | 'restore' | 'delete' | 'anonymize', string> = {
  suspend: 'Suspend',
  archive: 'Archive',
  restore: 'Restore',
  delete: 'Delete',
  anonymize: 'Anonymize',
}
const datetimePayloadValue = (value: string) => value ? new Date(value).toISOString() : null
const readable = (value?: string | null) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-'
const friendlyList = (values?: string[]) => values?.filter(Boolean).join(', ') || 'Not provided'
const objectSummary = (value?: Record<string, unknown>) => {
  if (!value || Object.keys(value).length === 0) return 'Not provided'
  return Object.entries(value)
    .filter(([, entry]) => entry !== null && entry !== undefined && String(entry).trim() !== '')
    .map(([key, entry]) => `${readable(key)}: ${String(entry)}`)
    .join(' | ') || 'Not provided'
}
const availabilitySummary = (value?: Record<string, unknown>) => {
  if (!value || Object.keys(value).length === 0) return 'Not provided'
  const days = Object.entries(value)
    .filter(([, entry]) => Array.isArray(entry) ? entry.length > 0 : Boolean(entry))
    .map(([key, entry]) => `${readable(key)} ${Array.isArray(entry) ? entry.join(', ') : String(entry)}`)
  return days.join(' | ') || objectSummary(value)
}
const formatWindowTime = (start?: string | null, end?: string | null) => [start, end].filter(Boolean).join(' - ') || 'Any time'
const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const availabilityWindowLabel = (window: any, t: (text: string, vars?: Record<string, string | number>) => string) => {
  const weekday = window.weekday === null || window.weekday === undefined ? t('One-off') : t(weekdayNames[Number(window.weekday)] || `Day ${window.weekday}`)
  return `${weekday}: ${t(formatWindowTime(window.start_time || window.starts_at, window.end_time || window.ends_at))}${window.timezone ? ` ${window.timezone}` : ''}`
}
const workerSkillLabel = (status?: string | null, source?: string | null) => {
  if (status === 'verified') return 'Verified'
  if (status === 'pending_verification') return 'Pending Verification'
  if (status === 'learned_through_mentorship' || source === 'mentorship') return 'Learned Through Mentorship'
  return 'Self Reported'
}
const workerSkillToCatalogResult = (skill: WorkerCanonicalSkill): SkillCatalogResult => ({
  id: skill.skill_id,
  canonicalName: skill.canonical_name,
  category: skill.category_name ? { id: skill.category_name, name: skill.category_name } : null,
  industries: [],
  aliases: [],
  status: skill.skill_status || 'active',
  matchType: 'exact',
  confidence: 1,
})
const hasAvailabilitySummary = (value?: Record<string, unknown>) => Boolean(value && Object.keys(value).length > 0)
const scoreLabel = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return '-'
  const score = Number(value)
  return Number.isFinite(score) ? `${Math.round(score)}` : '-'
}
const skillLevels = ['apprentice', 'novice', 'proficient', 'expert', 'master', 'mentor']
const skillLevelLabel = (value?: string | null) => readable(value || 'novice')
const evidenceTypeOrder = ['self_claim', 'verified_certificate', 'completed_shift', 'mentorship_session', 'supervisor_review', 'admin_adjustment']
const evidenceSummaryLabel = (summary?: Record<string, number>) => {
  const entries = Object.entries(summary || {}).filter(([, count]) => Number(count) > 0)
  return entries.length ? entries.map(([type, count]) => `${readable(type)}: ${count}`).join(' | ') : 'No evidence yet'
}
const evidenceSourceLabel = (evidence: WorkerSkillEvidence) => {
  if (evidence.sourceDocumentName) return evidence.sourceDocumentName
  if (evidence.sourceJobAssignmentId) return 'Job assignment'
  if (evidence.sourceJobShiftId) return 'Shift'
  if (evidence.sourceMentorshipSessionId) return 'Mentorship session'
  if (evidence.sourceReviewId) return 'Supervisor review'
  return ''
}
const evidenceMetadataSummary = (metadata?: Record<string, unknown>) => {
  const entries = Object.entries(metadata || {})
    .filter(([key, value]) => !['linkId', 'levelFloorRank'].includes(key) && value !== null && value !== undefined && String(value).trim() !== '')
    .slice(0, 3)
  return entries.map(([key, value]) => `${readable(key)}: ${String(value)}`).join(' | ')
}
const learningActivityRecency = (completedAt?: string | null) => {
  if (!completedAt) return 'No recent learning activity'
  const timestamp = new Date(completedAt).getTime()
  if (Number.isNaN(timestamp)) return formatDate(completedAt)
  const days = Math.max(0, Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24)))
  if (days === 0) return 'Active today'
  if (days === 1) return 'Active yesterday'
  return `${days} days ago`
}
const documentStatus = (document: WorkerProfileDocument) => {
  if (document.revokedAt || document.status === 'revoked') return 'revoked'
  if (document.archivedAt || document.isArchived || document.isActive === false || document.status === 'archived') return 'archived'
  if (document.expiryDate) {
    const expiry = new Date(`${document.expiryDate}T23:59:59.999Z`)
    if (!Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now()) return 'expired'
  }
  return document.verificationStatus || document.status || 'pending'
}

const documentRemoveLabel = (document: WorkerProfileDocument) => {
  const status = documentStatus(document)
  if (status === 'verified') return 'Revoke'
  if (document.documentType === 'profile_photo') return 'Remove'
  return ['pending', 'rejected', 'draft', 'submitted'].includes(status) ? 'Remove' : 'Archive'
}

const canEditDocumentSkillLinks = (document: WorkerProfileDocument) => {
  const status = documentStatus(document)
  const documentType = String(document.documentType || '').toLowerCase()
  return documentType !== 'profile_photo' && ['pending', 'submitted', 'draft', 'verified'].includes(status)
}

const skillCatalogResultFromDocumentLink = (link: NonNullable<WorkerProfileDocument['linkedSkills']>[number]): SkillCatalogResult => ({
  id: link.skillId,
  canonicalName: link.canonicalName,
  status: 'active',
  matchType: 'exact',
  confidence: 1,
})

const workerName = (worker: WorkerOrgRelationship | FreeAgentWorkerProfile | WorkerProfileDetail) => {
  if ('workerName' in worker && worker.workerName) return worker.workerName
  if ('display_name' in worker && worker.display_name) return worker.display_name
  if ('first_name' in worker || 'last_name' in worker) {
    const name = [worker.first_name, worker.last_name].filter(Boolean).join(' ')
    if (name) return name
  }
  return 'Worker profile'
}

const workerInitials = (worker: WorkerOrgRelationship | FreeAgentWorkerProfile | WorkerProfileDetail) => {
  const label = workerName(worker)
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  return initials || 'W'
}

const compactWorkerId = (worker: WorkerProfileDetail) => {
  const email = worker.workerEmail || worker.worker_email
  if (email) return email
  const phone = worker.phone || worker.worker_phone
  if (phone) return phone
  return 'Worker profile'
}

const profileImageUrl = (worker: WorkerProfileDetail) => worker.profilePhotoUrl || worker.avatarUrl || ''

const relationshipLabelForWorker = (worker: WorkerProfileDetail) => {
  const current = (worker.relationships || []).find((relationship) => relationship.status === 'active') || worker.relationships?.[0]
  return current ? readable(current.relationshipType) : readable(worker.relationshipStatus || worker.relationship_status || 'direct staff')
}

const locationSummaryForWorker = (worker: WorkerProfileDetail) => {
  const locations = worker.locationNames || []
  if (worker.currentLocationName) return worker.currentLocationName
  if (worker.preferredLocations?.[0]?.locationName) return worker.preferredLocations[0].locationName
  if (locations.length === 1) return locations[0]
  if (locations.length > 1) return `${locations[0]} +${locations.length - 1}`
  return 'No location assigned'
}

const assignmentSummaryForWorker = (worker: WorkerProfileDetail) => {
  if (worker.currentLocationName) return `Currently at ${worker.currentLocationName}`
  if (worker.preferredLocations?.length) return `${worker.preferredLocations.length} preferred work area${worker.preferredLocations.length === 1 ? '' : 's'}`
  return 'No current assignment summary'
}

const requirementStateForWorker = (worker: WorkerProfileDetail, risks: WorkerDocumentRisk[]) => {
  const workerRisks = risks.filter((risk) => risk.workerProfileId === worker.worker_profile_id)
  if (workerRisks.some((risk) => risk.riskStatus === 'expired')) return 'Expired'
  if (workerRisks.some((risk) => risk.riskStatus === 'expiring_soon')) return 'Expiring'
  if (workerRisks.some((risk) => risk.riskStatus === 'pending_review' || risk.riskStatus === 'rejected')) return 'Review needed'
  if ((worker.documents || []).length === 0) return 'No documents visible'
  return 'No visible document risks'
}

const requirementTone = (state: string) => {
  if (state === 'Expired') return 'status-pill--cancelled'
  if (state === 'Expiring' || state === 'Review needed') return 'status-pill--submitted'
  return ''
}

const statusToneClass = (status?: string | null) => `status-pill status-pill--${String(status || 'punched_out').replace(/[^a-z0-9_]+/gi, '_')}`

const sanitizedError = (value?: string) => {
  if (!value) return ''
  if (/at\s+\w+|stack|select\s+.+\s+from|uuid|postgres|syntax error/i.test(value)) return 'Unable to load Staff data. Check your access scope and try again.'
  return value
}

const matchesFilter = (relationship: WorkerOrgRelationship, filter: WorkerFilter) => {
  if (filter === 'own') return relationship.relationship_type === 'agency_worker'
  if (filter === 'company') return relationship.relationship_type === 'company_employee' || relationship.relationship_type === 'company_contractor'
  if (filter === 'shared') return relationship.relationship_type === 'shared_worker'
  if (filter === 'exclusive') return relationship.relationship_type === 'exclusive_worker'
  if (filter === 'pending') return relationship.status !== 'active'
  return true
}

const isRowInteractiveTarget = (target: EventTarget | null) => (
  target instanceof HTMLElement
    ? Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-row-action]'))
    : false
)

export const WorkersPanel: React.FC<WorkersPanelProps> = ({
  organizations,
  selectedOrgId,
  canViewWorkers,
  canManageWorkers,
  canShareWorkers,
  canManageDocumentConsent,
  requiresExplicitOrgContext = false,
  initialSelectedWorkerProfileId,
  onClearInitialSelectedWorkerProfileId,
  onSelectOrg,
}) => {
  const { t } = useI18n()
  const { accessContext, selectedTenantId, hasAnyPermission, loading: accessLoading } = useAccessContext()
  const auth = useContext(AuthContext)
  const riskViewerId = accessContext?.currentUser?.id || ''
  const riskTenantId = selectedTenantId || accessContext?.tenantId || ''
  const canManageOperationalDocuments = Boolean(canManageWorkers || accessContext?.isSystemSuperAdmin || hasAnyPermission(['tenant.worker_documents.manage']))
  const { isSimple, isAdvanced } = useAdminExperience()
  const industryLabels = useIndustryTerminology()
  const [activeSurface, setActiveSurface] = useState<StaffSurface>('staff')
  const [filter, setFilter] = useState<WorkerFilter>('own')
  const [relationships, setRelationships] = useState<WorkerOrgRelationship[]>([])
  const [selectedWorkerProfileId, setSelectedWorkerProfileId] = useState('')
  const [selectedWorker, setSelectedWorker] = useState<WorkerProfileDetail | null>(null)
  const [historyWorkerProfileId, setHistoryWorkerProfileId] = useState('')
  const [activeWorkerDetailTab, setActiveWorkerDetailTab] = useState<WorkerDetailTab>('overview')
  const [selectedWorkerCanonicalSkills, setSelectedWorkerCanonicalSkills] = useState<WorkerCanonicalSkill[]>([])
  const [selectedWorkerRecommendations, setSelectedWorkerRecommendations] = useState<SkillRecommendation[]>([])
  const [selectedWorkerSkillExperience, setSelectedWorkerSkillExperience] = useState<WorkerSkillExperience[]>([])
  const [selectedWorkerSkillProgression, setSelectedWorkerSkillProgression] = useState<WorkerSkillProgression[]>([])
  const [selectedWorkerSkillEvidence, setSelectedWorkerSkillEvidence] = useState<WorkerSkillEvidence[]>([])
  const [selectedEvidenceSkillId, setSelectedEvidenceSkillId] = useState('')
  const [skillEvidenceLoading, setSkillEvidenceLoading] = useState(false)
  const [selectedReliability, setSelectedReliability] = useState<WorkerReliabilityProfile | null>(null)
  const [selectedAvailability, setSelectedAvailability] = useState<WorkerAvailabilitySummary | null>(null)
  const [selectedAttendanceEvents, setSelectedAttendanceEvents] = useState<WorkerAttendanceEvent[]>([])
  const [selectedReviews, setSelectedReviews] = useState<WorkerSupervisorReview[]>([])
  const [selectedReliabilityError, setSelectedReliabilityError] = useState('')
  const [selectedAvailabilityError, setSelectedAvailabilityError] = useState('')
  const [reliabilityByWorker, setReliabilityByWorker] = useState<Record<string, WorkerReliabilityProfile>>({})
  const [skills, setSkills] = useState<Skill[]>([])
  const [skillsResolved, setSkillsResolved] = useState(false)
  const [profileDraft, setProfileDraft] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    publicHeadline: '',
    bio: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
    workRadiusKm: '25',
    locationConsent: false,
  })
  const [inviteCodes, setInviteCodes] = useState<WorkerInviteCode[]>([])
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [createdInvite, setCreatedInvite] = useState<WorkerInviteCode | null>(null)
  const [inviteForm, setInviteForm] = useState({ email: '', relationshipType: 'agency_worker', expiryDays: '7' })
  const [inviteLoading, setInviteLoading] = useState(false)
  const [riskSnapshot, setRiskSnapshot] = useState<{ viewerId: string; tenantId: string; token: string | null; context: unknown; rows: WorkerDocumentRisk[] } | null>(null)
  const documentRisksKnown = Boolean(canManageOperationalDocuments && !accessLoading && !auth?.loading && auth?.user?.id === riskViewerId && riskSnapshot && riskSnapshot.context === accessContext && riskSnapshot.viewerId === riskViewerId && riskSnapshot.tenantId === riskTenantId && riskSnapshot.token === getApiAuthToken())
  const documentRisks = documentRisksKnown ? riskSnapshot!.rows : []
  const [freeAgents, setFreeAgents] = useState<FreeAgentWorkerProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showSkillsModal, setShowSkillsModal] = useState(false)
  const [showRelationshipModal, setShowRelationshipModal] = useState(false)
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)
  const [verifyTarget, setVerifyTarget] = useState<WorkerProfileDocument | null>(null)
  const [rejectTarget, setRejectTarget] = useState<WorkerProfileDocument | null>(null)
  const [skillLinksTarget, setSkillLinksTarget] = useState<WorkerProfileDocument | null>(null)
  const [documentEditTarget, setDocumentEditTarget] = useState<WorkerProfileDocument | null>(null)
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null)
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [documentReviewForm, setDocumentReviewForm] = useState({ reason: '', notes: '' })
  const [documentSkillLinkSelection, setDocumentSkillLinkSelection] = useState<SkillCatalogResult[]>([])
  const [documentSkillLinkLevelFloor, setDocumentSkillLinkLevelFloor] = useState('proficient')
  const [documentForm, setDocumentForm] = useState({ documentName: '', documentType: 'iqama', issuingAuthority: '', issueDate: '', expiryDate: '', status: 'submitted', notes: '' })
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState<{ workerProfileId: string; workerName: string; action: 'suspend' | 'archive' | 'restore' | 'delete' | 'anonymize' } | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [form, setForm] = useState<WorkerRelationshipPayload>({
    workerProfileId: '',
    relationshipType: 'agency_worker',
    status: 'active',
    exclusiveUntil: null,
  })
  const [exclusiveUntilDraft, setExclusiveUntilDraft] = useState('')
  const [statusDrafts, setStatusDrafts] = useState<Record<string, WorkerRelationshipStatus>>({})
  const [typeDrafts, setTypeDrafts] = useState<Record<string, WorkerRelationshipType>>({})
  const [visibilityForm, setVisibilityForm] = useState<WorkerProfileVisibilityPayload & { workerProfileId: string }>({
    workerProfileId: '',
    publicHeadline: '',
    bio: '',
    isFreeAgent: true,
    marketplaceVisibility: 'visible_to_all',
  })
  const canLoadWorkers = canViewWorkers && (!requiresExplicitOrgContext || Boolean(selectedOrgId));
  const tableDefinition = useMemo(() => workerTableDefinition(skills, canManageOperationalDocuments, skillsResolved), [skills, canManageOperationalDocuments, skillsResolved])
  const workerList = useSmartTable<WorkerProfileDetail, WorkerTableFilters>(tableDefinition, {
    enabled: canLoadWorkers, lockedFilters: { orgId: selectedOrgId || undefined },
    fetchPage: (query, signal) => canLoadWorkers
      ? listWorkerProfilesPage({ ...query }, signal)
      : Promise.resolve({
        items: [],
        pagination: { page: query.page || 1, pageSize: query.pageSize || 25, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'asc' },
        filters: { ...query },
      }),
  })
  const statusFilter = workerList.filters.status || '', skillFilter = workerList.filters.skillId || ''
  const documentRiskFilter = (workerList.filters.documentRisk || '') as DocumentRiskFilter
  const setStatusFilter = (value: string) => workerList.setFilter('status', value || undefined)
  const setSkillFilter = (value: string) => workerList.setFilter('skillId', value || undefined)
  const setDocumentRiskFilter = (value: DocumentRiskFilter) => workerList.setFilter('documentRisk', value || undefined)
  const workerProfiles = workerList.items
  const commandSearchRef = useRef<HTMLInputElement>(null)
  useListCommands({ page: 'workers', enabled: activeSurface === 'staff' && canLoadWorkers && !selectedWorker,
    search: commandSearchRef, refresh: workerList.refreshResult,
    refreshReady: workerList.search === (workerList.query.search || '') })
  const ownsGlobalProfile = (worker?: WorkerProfileDetail | null) => Boolean(
    accessContext?.currentUser?.id && worker?.worker_user_id === accessContext.currentUser.id,
  )
  const canEditGlobalProfile = (worker?: WorkerProfileDetail | null) => Boolean(worker && (ownsGlobalProfile(worker) || accessContext?.isSystemSuperAdmin))
  const canEditSelectedGlobalProfile = canEditGlobalProfile(selectedWorker)
  const ownedWorkerProfiles = workerProfiles.filter(canEditGlobalProfile)
  const canEditVisibility = canEditGlobalProfile(workerProfiles.find(worker => worker.worker_profile_id === visibilityForm.workerProfileId))
    || (selectedWorker?.worker_profile_id === visibilityForm.workerProfileId && canEditSelectedGlobalProfile)

  useEffect(() => {
    if (!initialSelectedWorkerProfileId) return
    setSelectedWorkerProfileId(initialSelectedWorkerProfileId)
    onClearInitialSelectedWorkerProfileId?.()
  }, [initialSelectedWorkerProfileId, onClearInitialSelectedWorkerProfileId])

  useEffect(() => {
    if (!selectedOrgId || !canLoadWorkers) {
      setRelationships([])
      setFreeAgents([])
      return
    }
    let mounted = true
    setLoading(true)
    setError(t(''))
    Promise.all([listOrgWorkers(selectedOrgId), listFreeAgents(selectedOrgId)])
      .then(([relationshipRows, freeAgentRows]) => {
        if (!mounted) return
        setRelationships(relationshipRows)
        setFreeAgents(freeAgentRows)
        setStatusDrafts(Object.fromEntries(relationshipRows.map((row) => [row.id, row.status as WorkerRelationshipStatus])))
        setTypeDrafts(Object.fromEntries(relationshipRows.map((row) => [row.id, row.relationship_type as WorkerRelationshipType])))
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load workers.')))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [canLoadWorkers, selectedOrgId])


  useEffect(() => {
    setRiskSnapshot(null)
    if (!canLoadWorkers || !canManageOperationalDocuments || accessLoading || auth?.loading || auth?.user?.id !== riskViewerId || !riskTenantId || !riskViewerId) return
    let active = true; const controller = new AbortController(); const token = getApiAuthToken()
    void listWorkerDocumentRisks(30, riskTenantId, controller.signal)
      .then(rows => { if (active && token === getApiAuthToken()) setRiskSnapshot({ viewerId: riskViewerId, tenantId: riskTenantId, token, context: accessContext, rows }) })
      .catch(() => { /* Denied or unavailable assessments remain unknown, never zero risk. */ })
    return () => { active = false; controller.abort() }
  }, [canLoadWorkers, canManageOperationalDocuments, riskViewerId, riskTenantId, accessLoading, auth?.loading, auth?.user?.id, accessContext])

  useEffect(() => {
    if (!workerProfiles.length) {
      setReliabilityByWorker({})
      return
    }
    let mounted = true
    Promise.all(workerProfiles.slice(0, 25).map((worker) => (
      getWorkerReliability(worker.worker_profile_id)
        .then((reliability) => ({ workerProfileId: worker.worker_profile_id, reliability }))
        .catch(() => ({ workerProfileId: worker.worker_profile_id, reliability: null }))
    ))).then((rows) => {
      if (!mounted) return
      setReliabilityByWorker(Object.fromEntries(rows.filter((row) => row.reliability).map((row) => [row.workerProfileId, row.reliability!])))
    })
    return () => { mounted = false }
  }, [workerProfiles])

  useEffect(() => {
    let mounted = true
    listSkills()
      .then((rows) => mounted && setSkills(rows))
      .catch(() => mounted && setSkills([]))
      .finally(() => { if (mounted) setSkillsResolved(true) })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    if (!selectedWorkerProfileId) {
      setSelectedWorker(null)
      setActiveWorkerDetailTab('overview')
      setSelectedWorkerCanonicalSkills([])
      setSelectedWorkerRecommendations([])
      setSelectedWorkerSkillExperience([])
      setSelectedWorkerSkillProgression([])
      setSelectedWorkerSkillEvidence([])
      setSelectedEvidenceSkillId('')
      setSkillEvidenceLoading(false)
      setSelectedReliability(null)
      setSelectedAvailability(null)
      setSelectedAttendanceEvents([])
      setSelectedReviews([])
      setSelectedReliabilityError('')
      setSelectedAvailabilityError('')
      return
    }
    let mounted = true
    setActiveWorkerDetailTab('overview')
    getWorkerProfile(selectedWorkerProfileId)
      .then((worker) => {
        if (!mounted) return
        setSelectedWorker(worker)
        if (canManageDocumentConsent) {
          listWorkerDocumentsForManager(worker.worker_profile_id)
            .then((documents) => mounted && setSelectedWorker((current) => current?.worker_profile_id === worker.worker_profile_id ? { ...current, documents, certifications: documents.filter((document) => String(document.documentType || '').toLowerCase().includes('cert')) } : current))
            .catch(() => undefined)
        }
        setProfileDraft({
          firstName: worker.first_name || '',
          lastName: worker.last_name || '',
          phone: worker.phone || worker.worker_phone || '',
          publicHeadline: worker.public_headline || '',
          bio: worker.bio || '',
          addressLine1: worker.addressLine1 || String(worker.address?.addressLine1 || worker.address?.address_line1 || ''),
          addressLine2: worker.addressLine2 || String(worker.address?.addressLine2 || worker.address?.address_line2 || ''),
          city: worker.city || String(worker.address?.city || ''),
          region: worker.region || String(worker.address?.region || ''),
          postalCode: worker.postalCode || String(worker.address?.postalCode || worker.address?.postal_code || ''),
          country: worker.country || String(worker.address?.country || ''),
          workRadiusKm: String(worker.workRadiusKm || 25),
          locationConsent: Boolean(worker.locationConsent),
        })
        setVisibilityForm((current) => ({
          ...current,
          workerProfileId: worker.worker_profile_id,
          publicHeadline: worker.public_headline || '',
          bio: worker.bio || '',
          isFreeAgent: Boolean(worker.is_free_agent),
          marketplaceVisibility: (worker.marketplace_visibility as WorkerProfileVisibilityPayload['marketplaceVisibility']) || 'private',
        }))
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load worker profile.')))
    skillService.listAdminWorkerSkills(selectedWorkerProfileId)
      .then((data) => mounted && setSelectedWorkerCanonicalSkills(data.skills || []))
      .catch(() => mounted && setSelectedWorkerCanonicalSkills([]))
    skillService.listAdminWorkerRecommendations(selectedWorkerProfileId)
      .then((data) => mounted && setSelectedWorkerRecommendations(data.recommendations || []))
      .catch(() => mounted && setSelectedWorkerRecommendations([]))
    skillService.listAdminWorkerSkillExperience(selectedWorkerProfileId)
      .then((data) => mounted && setSelectedWorkerSkillExperience(data.experience || []))
      .catch(() => mounted && setSelectedWorkerSkillExperience([]))
    skillService.listAdminWorkerSkillProgression(selectedWorkerProfileId)
      .then((data) => {
        if (!mounted) return
        const progression = data.progression || []
        setSelectedWorkerSkillProgression(progression)
        setSelectedEvidenceSkillId((current) => current || progression[0]?.skillId || '')
      })
      .catch(() => mounted && setSelectedWorkerSkillProgression([]))
    getWorkerReliability(selectedWorkerProfileId)
      .then((reliability) => {
        if (!mounted) return
        setSelectedReliabilityError('')
        setSelectedReliability(reliability)
        if (reliability) setReliabilityByWorker((current) => ({ ...current, [selectedWorkerProfileId]: reliability }))
      })
      .catch((err) => {
        if (!mounted) return
        setSelectedReliability(null)
        setSelectedReliabilityError(apiErrorMessage(err, t('Unable to load worker reliability.')))
      })
    getWorkerAvailabilityForAdmin(selectedWorkerProfileId)
      .then((availability) => {
        if (!mounted) return
        setSelectedAvailabilityError('')
        setSelectedAvailability(availability)
      })
      .catch((err) => {
        if (!mounted) return
        setSelectedAvailability(null)
        setSelectedAvailabilityError(apiErrorMessage(err, t('Unable to load worker availability.')))
      })
    Promise.all([
      listWorkerAttendanceEventsForAdmin(selectedWorkerProfileId).catch(() => []),
      listWorkerReviewsForAdmin(selectedWorkerProfileId).catch(() => []),
    ]).then(([attendanceEvents, reviews]) => {
      if (!mounted) return
      setSelectedAttendanceEvents(attendanceEvents)
      setSelectedReviews(reviews)
    })
    return () => { mounted = false }
  }, [canManageDocumentConsent, selectedWorkerProfileId])

  useEffect(() => {
    if (!selectedWorkerProfileId || !selectedEvidenceSkillId) {
      setSelectedWorkerSkillEvidence([])
      setSkillEvidenceLoading(false)
      return
    }
    let mounted = true
    setSkillEvidenceLoading(true)
    skillService.listAdminWorkerSkillEvidence(selectedWorkerProfileId, selectedEvidenceSkillId)
      .then((data) => mounted && setSelectedWorkerSkillEvidence(data.evidence || []))
      .catch(() => mounted && setSelectedWorkerSkillEvidence([]))
      .finally(() => mounted && setSkillEvidenceLoading(false))
    return () => { mounted = false }
  }, [selectedWorkerProfileId, selectedEvidenceSkillId])

  // Load invite codes whenever the org changes
  useEffect(() => {
    if (!canLoadWorkers || !canManageWorkers) {
      setInviteCodes([])
      return
    }
    let mounted = true
    listWorkerInviteCodes(selectedOrgId ? { orgId: selectedOrgId } : {})
      .then((rows) => mounted && setInviteCodes(rows))
      .catch(() => mounted && setInviteCodes([]))
    return () => { mounted = false }
  }, [canLoadWorkers, canManageWorkers, selectedOrgId])

  const loadInviteCodes = async () => {
    if (!canManageWorkers) return
    try {
      const rows = await listWorkerInviteCodes(selectedOrgId ? { orgId: selectedOrgId } : {})
      setInviteCodes(rows)
    } catch {
      // non-fatal
    }
  }

  const handleCreateInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !canManageWorkers) return
    setInviteLoading(true)
    setError(t(''))
    setMessage(t(''))
    try {
      const invite = await createWorkerInviteCode({
        organizationId: selectedOrgId,
        relationshipType: inviteForm.relationshipType || undefined,
        email: inviteForm.email.trim() || null,
        expiryDays: Number(inviteForm.expiryDays) || 7,
      })
      setCreatedInvite(invite)
      setInviteForm({ email: '', relationshipType: 'agency_worker', expiryDays: '7' })
      await loadInviteCodes()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to create invite code.'))
    } finally {
      setInviteLoading(false)
    }
  }

  const handleRevokeInvite = async (inviteCodeId: string) => {
    if (!canManageWorkers) return
    setError(t(''))
    setMessage(t(''))
    try {
      await revokeWorkerInviteCode(inviteCodeId)
      setMessage(t('Invite code revoked.'))
      await loadInviteCodes()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to revoke invite code.'))
    }
  }

  const copyToClipboard = (text: string, label = t('Copied')) => {
    navigator.clipboard.writeText(text).then(() => setMessage(label)).catch(() => setMessage(t('Copy failed — select and copy manually.')))
  }

  const refresh = async () => {
    if (!selectedOrgId || !canLoadWorkers) return
    const [relationshipRows, freeAgentRows] = await Promise.all([
      listOrgWorkers(selectedOrgId),
      listFreeAgents(selectedOrgId),
    ])
    setRelationships(relationshipRows)
    setFreeAgents(freeAgentRows)
    setStatusDrafts(Object.fromEntries(relationshipRows.map((row) => [row.id, row.status as WorkerRelationshipStatus])))
    setTypeDrafts(Object.fromEntries(relationshipRows.map((row) => [row.id, row.relationship_type as WorkerRelationshipType])))
    await workerList.refresh()
    if (selectedWorkerProfileId) {
      setSelectedWorker(await getWorkerProfile(selectedWorkerProfileId))
    }
    await loadInviteCodes()
  }

  const requestWorkerLifecycle = (workerProfileId: string, action: 'suspend' | 'archive' | 'restore' | 'delete' | 'anonymize', label?: string) => {
    if (!canManageWorkers) return
    setPendingLifecycleAction({ workerProfileId, action, workerName: label || t('this worker') })
  }

  const runWorkerLifecycle = async (workerProfileId: string, action: 'suspend' | 'archive' | 'restore' | 'delete' | 'anonymize') => {
    if (!canManageWorkers) return
    setError(t(''))
    setMessage(t(''))
    try {
      await runSystemWorkerLifecycleAction(workerProfileId, action, `${action} requested from Systems Admin`)
      await refresh()
      setMessage(action === 'delete' ? t('Worker safely deleted.') : t('Worker {action} complete.', { action: t(workerLifecycleActionLabels[action]) }))
      setPendingLifecycleAction(null)
    } catch (err: any) {
      const data = err?.response?.data
      const dependencies = Array.isArray(data?.dependencies)
        ? ` ${t('Dependencies: {dependencies}.', { dependencies: data.dependencies.map((item: any) => `${item.type}: ${item.count}`).join(', ') })}`
        : ''
      setError((data?.message || t('Unable to {action} worker.', { action: t(workerLifecycleActionLabels[action]) })) + dependencies)
    }
  }

  const visibleRelationships = useMemo(
    () => relationships.filter((relationship) => matchesFilter(relationship, filter)),
    [filter, relationships],
  )
  const workerProfilesByDocumentRisk = useMemo(() => {
    if (!documentRisksKnown || !documentRiskFilter) return workerProfiles
    const riskyIds = new Set(documentRisks
      .filter((risk) => {
        if (documentRiskFilter === 'expired') return risk.riskStatus === 'expired'
        if (documentRiskFilter === 'expiring_soon') return risk.riskStatus === 'expiring_soon'
        return risk.riskStatus === 'pending_review' || risk.riskStatus === 'rejected'
      })
      .map((risk) => risk.workerProfileId))
    return workerProfiles.filter((worker) => riskyIds.has(worker.worker_profile_id))
  }, [documentRiskFilter, documentRisks, documentRisksKnown, workerProfiles])

  const relationshipWriteAllowed = canManageWorkers || (
    canShareWorkers && ['shared_worker', 'exclusive_worker'].includes(form.relationshipType)
  )

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!relationshipWriteAllowed) return
    if (!form.workerProfileId?.trim()) {
      setError(t('Choose a worker from the directory before creating a relationship.'))
      return
    }
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await createWorkerOrgRelationship(selectedOrgId, {
        ...form,
        workerProfileId: form.workerProfileId?.trim() || undefined,
        workerUserId: form.workerUserId?.trim() || undefined,
        exclusiveUntil: form.relationshipType === 'exclusive_worker' ? datetimePayloadValue(exclusiveUntilDraft) : null,
      })
      await refresh()
      setForm({ workerProfileId: '', relationshipType: 'agency_worker', status: 'active', exclusiveUntil: null })
      setExclusiveUntilDraft('')
      setShowRelationshipModal(false)
      setMessage(t('Worker relationship created.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to create worker relationship.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRelationshipUpdate = async (relationship: WorkerOrgRelationship) => {
    const relationshipType = typeDrafts[relationship.id] || (relationship.relationship_type as WorkerRelationshipType)
    const canUpdate = canManageWorkers || (canShareWorkers && ['shared_worker', 'exclusive_worker'].includes(relationshipType))
    if (!canUpdate) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await updateWorkerOrgRelationship(relationship.id, {
        relationshipType,
        status: statusDrafts[relationship.id] || relationship.status as WorkerRelationshipStatus,
        exclusiveUntil: relationshipType === 'exclusive_worker' ? relationship.exclusive_until || null : null,
      })
      await refresh()
      setMessage(t('Worker relationship updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update worker relationship.'))
    } finally {
      setSaving(false)
    }
  }

  const handleEndRelationship = async (relationship: WorkerOrgRelationship) => {
    if (!canManageWorkers) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await endWorkerOrgRelationship(relationship.id)
      await refresh()
      setMessage(t('Worker relationship ended.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to end worker relationship.'))
    } finally {
      setSaving(false)
    }
  }

  const handleVisibilityUpdate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canEditVisibility) return
    if (!visibilityForm.workerProfileId.trim()) {
      setError(t('Choose a worker before updating visibility.'))
      return
    }
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await updateWorkerProfileVisibility(visibilityForm.workerProfileId.trim(), {
        publicHeadline: visibilityForm.publicHeadline || null,
        bio: visibilityForm.bio || null,
        isFreeAgent: visibilityForm.isFreeAgent,
        marketplaceVisibility: visibilityForm.marketplaceVisibility,
      })
      await refresh()
      setShowVisibilityModal(false)
      setMessage(t('Worker profile visibility updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update worker profile visibility.'))
    } finally {
      setSaving(false)
    }
  }

  const handleProfileUpdate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedWorker || !canEditSelectedGlobalProfile) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      const worker = await updateWorkerProfile(selectedWorker.worker_profile_id, {
        firstName: profileDraft.firstName || null,
        lastName: profileDraft.lastName || null,
        phone: profileDraft.phone || null,
        publicHeadline: profileDraft.publicHeadline || null,
        bio: profileDraft.bio || null,
        addressLine1: profileDraft.addressLine1 || null,
        addressLine2: profileDraft.addressLine2 || null,
        city: profileDraft.city || null,
        region: profileDraft.region || null,
        postalCode: profileDraft.postalCode || null,
        country: profileDraft.country || null,
        workRadiusKm: Number(profileDraft.workRadiusKm || 25),
        locationConsent: profileDraft.locationConsent,
      })
      setSelectedWorker(worker)
      await refresh()
      setShowProfileModal(false)
      setMessage(t('Worker profile updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update worker profile.'))
    } finally {
      setSaving(false)
    }
  }

  const reloadSelectedWorkerCanonicalSkills = async (workerProfileId: string) => {
    const data = await skillService.listAdminWorkerSkills(workerProfileId)
    setSelectedWorkerCanonicalSkills(data.skills || [])
    const recommendations = await skillService.listAdminWorkerRecommendations(workerProfileId)
    setSelectedWorkerRecommendations(recommendations.recommendations || [])
    const experience = await skillService.listAdminWorkerSkillExperience(workerProfileId)
    setSelectedWorkerSkillExperience(experience.experience || [])
  }

  const handleAddRecommendedSkill = async (recommendation: SkillRecommendation) => {
    if (!selectedWorker || !canManageWorkers || saving) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await skillService.addAdminWorkerSkill(selectedWorker.worker_profile_id, recommendation.skillId, { source: 'admin_added' })
      const worker = await getWorkerProfile(selectedWorker.worker_profile_id)
      setSelectedWorker(worker)
      await reloadSelectedWorkerCanonicalSkills(selectedWorker.worker_profile_id)
      await refresh()
      setMessage(t('Recommended skill added.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to add recommended skill.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveSkill = async (skillId?: string) => {
    if (!selectedWorker || !skillId || !canManageWorkers) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await skillService.removeAdminWorkerSkill(selectedWorker.worker_profile_id, skillId)
      const worker = await getWorkerProfile(selectedWorker.worker_profile_id)
      setSelectedWorker(worker)
      await reloadSelectedWorkerCanonicalSkills(selectedWorker.worker_profile_id)
      await refresh()
      setMessage(t('Worker skill removed.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to remove worker skill.'))
    } finally {
      setSaving(false)
    }
  }

  const handleCanonicalSkillsChange = async (nextSkills: SkillCatalogResult[]) => {
    if (!selectedWorker || !canManageWorkers || saving) return
    const currentIds = new Set(selectedWorkerCanonicalSkills.map((skill) => skill.skill_id))
    const nextIds = new Set(nextSkills.map((skill) => skill.id))
    const added = nextSkills.find((skill) => !currentIds.has(skill.id))
    const removed = selectedWorkerCanonicalSkills.find((skill) => !nextIds.has(skill.skill_id))
    if (!added && !removed) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      if (added) {
        await skillService.addAdminWorkerSkill(selectedWorker.worker_profile_id, added.id, { source: 'admin_added' })
        setMessage(t('Worker skill added.'))
      } else if (removed) {
        await skillService.removeAdminWorkerSkill(selectedWorker.worker_profile_id, removed.skill_id)
        setMessage(t('Worker skill removed.'))
      }
      const worker = await getWorkerProfile(selectedWorker.worker_profile_id)
      setSelectedWorker(worker)
      await reloadSelectedWorkerCanonicalSkills(selectedWorker.worker_profile_id)
      await refresh()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update worker skill.'))
    } finally {
      setSaving(false)
    }
  }

  const refreshWorkerDocuments = async () => {
    if (!selectedWorker || !canManageDocumentConsent) return
    const documents = await listWorkerDocumentsForManager(selectedWorker.worker_profile_id)
    setSelectedWorker({ ...selectedWorker, documents, certifications: documents.filter((document) => String(document.documentType || '').toLowerCase().includes('cert')) })
  }

  const refreshWorkerSkillProgression = async (workerProfileId: string) => {
    const data = await skillService.listAdminWorkerSkillProgression(workerProfileId)
    const progression = data.progression || []
    setSelectedWorkerSkillProgression(progression)
    setSelectedEvidenceSkillId((current) => current || progression[0]?.skillId || '')
  }

  const handleVerifyDocument = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (!selectedWorker || !verifyTarget || !canManageDocumentConsent) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      const skillLinks = documentSkillLinkSelection.map((skill) => ({
        skillId: skill.id,
        levelFloor: documentSkillLinkLevelFloor || undefined,
      }))
      await verifyWorkerDocument(selectedWorker.worker_profile_id, verifyTarget.documentId, documentReviewForm.notes || undefined, skillLinks)
      await refreshWorkerDocuments()
      await refreshWorkerSkillProgression(selectedWorker.worker_profile_id).catch(() => undefined)
      setVerifyTarget(null)
      setDocumentReviewForm({ reason: '', notes: '' })
      setDocumentSkillLinkSelection([])
      setDocumentSkillLinkLevelFloor('proficient')
      setMessage(t('Worker document verified.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to verify worker document.'))
    } finally {
      setSaving(false)
    }
  }

  const openSkillLinksModal = (document: WorkerProfileDocument) => {
    setSkillLinksTarget(document)
    setDocumentSkillLinkSelection((document.linkedSkills || []).map(skillCatalogResultFromDocumentLink))
    setDocumentSkillLinkLevelFloor(document.linkedSkills?.[0]?.levelFloor || 'proficient')
  }

  const handleSaveDocumentSkillLinks = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (!selectedWorker || !skillLinksTarget || !canManageDocumentConsent) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      const skillLinks = documentSkillLinkSelection.map((skill) => ({
        skillId: skill.id,
        levelFloor: documentSkillLinkLevelFloor || undefined,
      }))
      await replaceWorkerDocumentSkillLinks(selectedWorker.worker_profile_id, skillLinksTarget.documentId, skillLinks)
      await refreshWorkerDocuments()
      await refreshWorkerSkillProgression(selectedWorker.worker_profile_id).catch(() => undefined)
      if (selectedEvidenceSkillId) {
        await skillService.listAdminWorkerSkillEvidence(selectedWorker.worker_profile_id, selectedEvidenceSkillId)
          .then((data) => setSelectedWorkerSkillEvidence(data.evidence || []))
          .catch(() => undefined)
      }
      setSkillLinksTarget(null)
      setDocumentSkillLinkSelection([])
      setDocumentSkillLinkLevelFloor('proficient')
      setMessage(t('Linked skills updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update linked skills.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRejectDocument = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedWorker || !rejectTarget || !canManageDocumentConsent) return
    if (!documentReviewForm.reason.trim()) {
      setError(t('Rejection reason is required.'))
      return
    }
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      await rejectWorkerDocument(selectedWorker.worker_profile_id, rejectTarget.documentId, documentReviewForm.reason.trim(), documentReviewForm.notes || undefined)
      await refreshWorkerDocuments()
      setRejectTarget(null)
      setDocumentReviewForm({ reason: '', notes: '' })
      setMessage(t('Worker document rejected.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to reject worker document.'))
    } finally {
      setSaving(false)
    }
  }

  const openDocumentModal = (document?: WorkerProfileDocument) => {
    setDocumentEditTarget(document || null)
    setDocumentForm({
      documentName: document?.documentName || '',
      documentType: document?.documentType || 'other',
      issuingAuthority: document?.issuingAuthority || '',
      issueDate: document?.issueDate || '',
      expiryDate: document?.expiryDate || '',
      status: document?.status || 'submitted',
      notes: document?.notes || '',
    })
    setShowDocumentModal(true)
  }

  const handleOpenDocumentFile = async (document: WorkerProfileDocument) => {
    if (!document.fileUrl || openingDocumentId === document.documentId) return
    setError(t(''))
    setMessage(t(''))
    setOpeningDocumentId(document.documentId)
    try {
      const { blob } = await downloadWorkerDocumentForManager(document.fileUrl)
      const blobUrl = URL.createObjectURL(blob)
      const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer')
      if (!opened) setMessage(t('Document opened in a protected blob URL. Allow popups if a new tab did not appear.'))
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (err: any) {
      const normalized = apiErrorMessage(err, t('Unable to open worker document file.'))
      const status = err?.response?.status
      const code = err?.response?.data?.error?.code || err?.response?.data?.code
      if (status === 401 || status === 403 || code === 'document_file_permission_denied') {
        setError(t('Permission denied'))
      } else if (status === 410 || code === 'document_archived') {
        setError(t('Document archived'))
      } else if (code === 'document_file_not_found') {
        setError(t('File missing from storage'))
      } else if (status === 404 || code === 'document_file_missing' || code === 'worker_document_not_found') {
        setError(t('File unavailable'))
      } else {
        setError(normalized)
      }
      if ([404, 410].includes(Number(status))) {
        await refreshWorkerDocuments().catch(() => undefined)
      }
    } finally {
      setOpeningDocumentId(null)
    }
  }

  const handleSaveDocument = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedWorker || !canManageDocumentConsent) return
    setSaving(true)
    setError(t(''))
    setMessage(t(''))
    try {
      const payload = {
        ...documentForm,
        issuingAuthority: documentForm.issuingAuthority || null,
        issueDate: documentForm.issueDate || null,
        expiryDate: documentForm.expiryDate || null,
        notes: documentForm.notes || null,
      }
      if (documentEditTarget) {
        await updateWorkerDocumentForManager(selectedWorker.worker_profile_id, documentEditTarget.documentId, payload)
      } else {
        await createWorkerDocumentForManager(selectedWorker.worker_profile_id, payload)
      }
      await refreshWorkerDocuments()
      setShowDocumentModal(false)
      setDocumentEditTarget(null)
      setMessage(documentEditTarget ? t('Worker document updated.') : t('Worker document added.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to save worker document.'))
    } finally {
      setSaving(false)
    }
  }

  const handleExpireDocument = async (document: WorkerProfileDocument) => {
    if (!selectedWorker || !canManageDocumentConsent) return
    setSaving(true)
    try {
      await expireWorkerDocument(selectedWorker.worker_profile_id, document.documentId)
      await refreshWorkerDocuments()
      setMessage(t('Worker document expired.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to expire worker document.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRevokeDocument = async (document: WorkerProfileDocument) => {
    if (!selectedWorker || !canManageDocumentConsent) return
    setSaving(true)
    try {
      const result = documentStatus(document) === 'verified'
        ? await revokeWorkerDocument(selectedWorker.worker_profile_id, document.documentId, 'Revoked from Admin Workers.')
        : await removeWorkerDocumentForManager(selectedWorker.worker_profile_id, document.documentId)
      await refreshWorkerDocuments()
      setMessage(result.message ? t(result.message) : t('Worker document removed.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to remove worker document.'))
    } finally {
      setSaving(false)
    }
  }

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const clearSelectedWorker = () => {
    setSelectedWorkerProfileId('')
    setSelectedWorker(null)
    setShowProfileModal(false)
    setShowSkillsModal(false)
    setShowVisibilityModal(false)
    setVerifyTarget(null)
    setRejectTarget(null)
    setShowDocumentModal(false)
    setDocumentEditTarget(null)
  }
  const openWorkerDetails = (workerProfileId: string) => {
    setSelectedWorkerProfileId(workerProfileId)
  }
  const handleWorkerRowClick = (event: React.MouseEvent<HTMLElement>, workerProfileId: string) => {
    if (isRowInteractiveTarget(event.target)) return
    openWorkerDetails(workerProfileId)
  }
  const handleWorkerRowKeyDown = (event: React.KeyboardEvent<HTMLElement>, workerProfileId: string) => {
    if (isRowInteractiveTarget(event.target)) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openWorkerDetails(workerProfileId)
    }
  }
  const handleSelectOrg = (orgId: string) => {
    clearSelectedWorker()
    onSelectOrg(orgId)
  }
  const handleVisibilityWorkerSelect = (workerProfileId: string) => {
    setVisibilityForm((current) => ({ ...current, workerProfileId }))
    if (!workerProfileId) return
    getWorkerProfile(workerProfileId)
      .then((worker) => {
        setVisibilityForm((current) => (
          current.workerProfileId === worker.worker_profile_id
            ? {
              ...current,
              publicHeadline: worker.public_headline || '',
              bio: worker.bio || '',
              isFreeAgent: Boolean(worker.is_free_agent),
              marketplaceVisibility: (worker.marketplace_visibility as WorkerProfileVisibilityPayload['marketplaceVisibility']) || 'private',
            }
            : current
        ))
      })
      .catch((err: any) => setError(err?.response?.data?.message || t('Unable to load worker visibility details.')))
  }

  const activeFilterCount = workerList.activeFilterCount
  const clearStaffFilters = workerList.resetFilters
  const visibleRiskWorkerIds = new Set(documentRisks.map((risk) => risk.workerProfileId))
  const staffOnPageWorking = workerProfiles.filter((worker) => worker.status === 'punched_in' || worker.status === 'break').length
  const staffOnPageWithLocations = workerProfiles.filter((worker) => worker.currentLocationName || worker.locationNames?.length || worker.preferredLocations?.length).length
  const visibleRequirementAttentionCount = workerProfiles.filter((worker) => visibleRiskWorkerIds.has(worker.worker_profile_id)).length
  const selectedOrgName = selectedOrg?.name || t('the selected organization')

  return (
    <div className="stack staff-management" data-testid="admin-workers-panel">
      <div className="staff-page-intro">
        <div className="form-row admin-select-row">
          <label htmlFor="workers-org">{t('Selected organization')}</label>
          <select id="workers-org" className="input" value={selectedOrgId} onChange={(event) => handleSelectOrg(event.target.value)}>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name} ({formatOrgType(org.org_type)})</option>)}
          </select>
        </div>
        <p className="helper">
          {t('Primary Staff shows only direct, active workers owned by {org}. Partner, shared, free-agent, and assignment-only workers stay out of this list.', { org: selectedOrgName })}
        </p>
        <div className="staff-surface-tabs" role="tablist" aria-label={t('Staff management views')}>
          {staffSurfaces.filter((surface) => !isSimple || ['staff','invites'].includes(surface.id)).map((surface) => {
            const hiddenForViewer = surface.id !== 'staff' && !canManageWorkers && !canShareWorkers
            if (hiddenForViewer) return null
            return (
              <button
                key={surface.id}
                type="button"
                role="tab"
                aria-selected={activeSurface === surface.id}
                className={`admin-filter ${activeSurface === surface.id ? 'active' : ''}`}
                onClick={() => setActiveSurface(surface.id)}
                title={t(surface.description)}
              >
                {t(surface.label)}
              </button>
            )
          })}
        </div>
      </div>

      {!canViewWorkers ? <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? `${industryLabels.label('worker')} roster access requires worker view or worker management permission.` : t('Worker roster access requires worker view or worker management permission.')}</p> : null}
      {canViewWorkers && requiresExplicitOrgContext && !selectedOrgId ? <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? `Select an organization to inspect ${industryLabels.label('workers').toLowerCase()}.` : t('Select an organization to inspect workers.')}</p> : null}
      {message ? <p className="helper">{message}</p> : null}
      <ErrorBanner message={sanitizedError(error || workerList.error)} />

      {activeSurface === 'staff' ? (
        <section className="staff-primary-surface stack" data-testid="staff-primary-list" aria-busy={workerList.loading}>
          <div className="staff-summary-grid" aria-label={t('Staff summary')}>
            <article className="panel stat-card">
              <span className="helper">{t('Total own staff')}</span>
              <strong>{workerList.loading && !workerList.items.length ? t('Loading') : workerList.pagination.total}</strong>
              <p className="helper">{t('Direct active relationships owned by {org}.', { org: selectedOrgName })}</p>
            </article>
            {isAdvanced ? <article className="panel stat-card">
              <span className="helper">{t('Active workers')}</span>
              <strong>{workerList.loading && !workerList.items.length ? t('Loading') : workerList.pagination.total}</strong>
              <p className="helper">{t('Active worker tenant membership plus active ownership relationship.')}</p>
            </article> : null}
            <article className="panel stat-card">
              <span className="helper">{t('Currently working')}</span>
              <strong>{workerList.loading && !workerList.items.length ? t('Loading') : staffOnPageWorking}</strong>
              <p className="helper">{t('Visible page rows with punched-in or break status.')}</p>
            </article>
            <article className="panel stat-card">
              <span className="helper">{t('Requirement attention')}</span>
              <strong>{documentRisksKnown ? visibleRequirementAttentionCount : t('Document risks unavailable')}</strong>
              <p className="helper">{t(documentRisksKnown ? 'Visible workers with expired, expiring, pending, or rejected documents.' : 'Operational document risks require document management permission.')}</p>
            </article>
            {isAdvanced ? <article className="panel stat-card">
              <span className="helper">{t('Assignment context')}</span>
              <strong>{workerList.loading && !workerList.items.length ? t('Loading') : staffOnPageWithLocations}</strong>
              <p className="helper">{t('Visible workers with current, preferred, or relationship location context.')}</p>
            </article> : null}
          </div>

          <TableQueryControls table={workerList} ariaLabel={t('Staff filters')} searchLabel={industryLabels.explicitIndustry ? `Search ${industryLabels.label('workers').toLowerCase()}` : t('Search staff')}
            searchRef={commandSearchRef} placeholder={t('Name, email, phone, reference')}>
              <div className="form-row">
                <label htmlFor="worker-status-filter">{t('Work status')}</label>
                <select id="worker-status-filter" className="input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">{t('Any status')}</option>
                  <option value="punched_in">{t('Currently working')}</option>
                  <option value="break">{t('On break')}</option>
                  <option value="punched_out">{t('Off shift')}</option>
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="worker-skill-filter">{t('Role / skill')}</label>
                <select id="worker-skill-filter" className="input" value={skillFilter} onChange={(event) => setSkillFilter(event.target.value)}>
                  <option value="">{t('Any role or skill')}</option>
                  {skills.map((skill) => <option key={skill.id} value={skill.id}>{skill.name}{skill.category ? ` (${skill.category})` : ''}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="worker-document-risk-filter">{t('Requirements')}</label>
                <select id="worker-document-risk-filter" disabled={!documentRisksKnown} className="input" value={documentRiskFilter} onChange={(event) => setDocumentRiskFilter(event.target.value as DocumentRiskFilter)}>
                  <option value="">{t('Any requirement state')}</option>
                  <option value="missing">{t('Review needed')}</option>
                  <option value="expired">{t('Expired')}</option>
                  <option value="expiring_soon">{t('Expiring soon')}</option>
                </select>
              </div>
          </TableQueryControls>
          <TableDisplayControls table={workerList} />

          {workerList.loading && !workerList.items.length ? <LoadingState label={t('Loading own staff...')} /> : null}
          {!workerList.loading && workerList.items.length === 0 && activeFilterCount === 0 && canLoadWorkers ? (
            <EmptyState
              title={t('No own staff in this organization')}
              description={canManageWorkers ? t('Invite a worker or create a direct ownership relationship for {org}.', { org: selectedOrgName }) : t('No direct staff are visible in your authorized scope.')}
              action={canManageWorkers ? <button type="button" className="btn btn-primary" onClick={() => { setActiveSurface('invites'); setCreatedInvite(null); setShowInviteModal(true) }}>{t('Invite worker')}</button> : null}
            />
          ) : null}
          {!workerList.loading && workerProfilesByDocumentRisk.length === 0 && activeFilterCount > 0 ? (
            <EmptyState
              title={industryLabels.explicitIndustry ? `No ${industryLabels.label('workers').toLowerCase()} match these filters` : t('No staff match these filters')}
              description={t('Try a different search, work status, role, or requirement filter.')}
              action={<button type="button" className="btn btn-ghost" onClick={clearStaffFilters}>{t('Clear filters')}</button>}
              variant="filtered"
            />
          ) : null}
          <AdminTableContainer label={t('Own staff list')} density={workerList.density.density}>
          {workerProfilesByDocumentRisk.length ? (
            <div className="staff-worker-list" role="list" aria-label={t('Own staff list')}>
              {workerProfilesByDocumentRisk.map((worker) => {
                const name = workerName(worker)
                const requirementState = requirementStateForWorker(worker, documentRisks)
                const reliability = scoreLabel(reliabilityByWorker[worker.worker_profile_id]?.reliability_score ?? worker.reliability_score)
                const actions: AdminRowAction[] = [
                  { id: 'view', label: t('View details'), onSelect: () => openWorkerDetails(worker.worker_profile_id) },
                  ...(canManageWorkers ? [
                    { id: 'edit', label: t('Edit profile'), disabled: !canEditGlobalProfile(worker), disabledReason: t('Global profile and visibility settings are managed by the account owner or system security administration.'), onSelect: () => { openWorkerDetails(worker.worker_profile_id); setShowProfileModal(true) } },
                    { id: 'skills', label: t('Manage skills'), onSelect: () => { openWorkerDetails(worker.worker_profile_id); setShowSkillsModal(true) } },
                    { id: 'suspend', label: t('Suspend'), onSelect: () => requestWorkerLifecycle(worker.worker_profile_id, 'suspend', name) },
                    { id: 'archive', label: t('Archive'), onSelect: () => requestWorkerLifecycle(worker.worker_profile_id, 'archive', name), destructive: true },
                    { id: 'anonymize', label: t('Anonymize'), onSelect: () => requestWorkerLifecycle(worker.worker_profile_id, 'anonymize', name), destructive: true },
                    { id: 'delete', label: t('Delete if safe'), onSelect: () => requestWorkerLifecycle(worker.worker_profile_id, 'delete', name), destructive: true },
                  ] : []),
                ]
                return (
                  <article
                    key={worker.worker_profile_id}
                    className="staff-worker-row"
                    role="listitem"
                    tabIndex={0}
                    aria-label={t('View details for {name}', { name })}
                    onClick={(event) => handleWorkerRowClick(event, worker.worker_profile_id)}
                    onKeyDown={(event) => handleWorkerRowKeyDown(event, worker.worker_profile_id)}
                  >
                    <div className="staff-worker-identity">
                      {profileImageUrl(worker) ? <img className="staff-worker-avatar" src={profileImageUrl(worker)} alt="" /> : <span className="staff-worker-avatar staff-worker-avatar--initials" aria-hidden="true">{workerInitials(worker)}</span>}
                      <div>
                        <strong>{name}</strong>
                        <p className="helper">{compactWorkerId(worker)}</p>
                      </div>
                    </div>
                    <div className="staff-worker-facts">
                      <span className={statusToneClass(worker.status)}>{t(readable(worker.status || 'punched_out'))}</span>
                      <span className="status-pill">{t(relationshipLabelForWorker(worker))}</span>
                      <span className={`status-pill ${requirementTone(requirementState)}`.trim()}>{t(requirementState)}</span>
                    </div>
                    <div className="staff-worker-context">
                      {workerList.preference.isVisible('role') ? <div><span className="helper">{t('Role')}</span><strong>{(worker.skillNames || [])[0] || t('Role not set')}</strong></div> : null}
                      {workerList.preference.isVisible('location') ? <div><span className="helper">{t('Location')}</span><strong>{locationSummaryForWorker(worker)}</strong></div> : null}
                      {workerList.preference.isVisible('assignment') ? <div><span className="helper">{t('Assignment')}</span><strong>{assignmentSummaryForWorker(worker)}</strong></div> : null}
                      {workerList.preference.isVisible('reliability') ? <div><span className="helper">{t('Reliability')}</span><strong>{reliability === '-' ? t('No data') : `${reliability}/100`}</strong></div> : null}
                    </div>
                    <AdminRowActionMenu recordName={name} actions={actions} />
                  </article>
                )
              })}
            </div>
          ) : null}
          </AdminTableContainer>
          <PaginationControls pagination={workerList.pagination} onPageChange={workerList.setPage} onPageSizeChange={workerList.setPageSize} />
        </section>
      ) : null}

      <DetailDrawer
        title={selectedWorker ? workerName(selectedWorker) : (industryLabels.explicitIndustry ? `${industryLabels.label('worker')} profile` : t('Worker profile'))}
        subtitle={selectedWorker ? `${selectedWorker.workerEmail || selectedWorker.phone || (industryLabels.explicitIndustry ? `${industryLabels.label('worker')} contact not available` : t('Worker contact not available'))} / ${selectedWorker.profileCompletion?.summary || t('Profile summary unavailable')}` : undefined}
        isOpen={Boolean(selectedWorker)}
        onClose={clearSelectedWorker}
        nonBlocking
        actions={selectedWorker ? (
          <>
            {canManageWorkers || canEditSelectedGlobalProfile ? <button type="button" className="btn btn-primary" disabled={!canEditSelectedGlobalProfile} title={t('Global profile and visibility settings are managed by the account owner or system security administration.')} onClick={() => setShowProfileModal(true)}>{t('Edit profile')}</button> : null}
            {canManageWorkers ? <button type="button" className="btn btn-secondary" onClick={() => setShowSkillsModal(true)}>{t('Manage skills')}</button> : null}
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryWorkerProfileId(selectedWorker.worker_profile_id)}>{t('Historical shifts')}</button>
            {canEditSelectedGlobalProfile ? <button type="button" className="btn btn-ghost" onClick={() => { setActiveSurface('invites'); setShowVisibilityModal(true) }}>{t('Visibility')}</button> : null}
            {canManageWorkers ? <button type="button" className="btn btn-ghost" onClick={() => requestWorkerLifecycle(selectedWorker.worker_profile_id, 'archive', workerName(selectedWorker))}>{t('Archive')}</button> : null}
            {canManageWorkers ? <button type="button" className="btn btn-ghost" onClick={() => requestWorkerLifecycle(selectedWorker.worker_profile_id, 'anonymize', workerName(selectedWorker))}>{t('Anonymize')}</button> : null}
          </>
        ) : null}
      >
        {!canEditSelectedGlobalProfile ? <p className="helper">{t('Global profile and visibility settings are managed by the account owner or system security administration.')}</p> : null}
        {selectedWorker ? (
          <div className="stack">
            <div className="inline-group">
              <span className="admin-filter active">{t(readable(selectedWorker.status))}</span>
              {scoreLabel(selectedReliability?.reliabilityScore ?? selectedReliability?.reliability_score ?? selectedWorker.reliability_score) === '-'
                ? <span className="admin-filter">{t('No reliability data yet')}</span>
                : <span className="admin-filter">{t('Reliability {score}/100', { score: scoreLabel(selectedReliability?.reliabilityScore ?? selectedReliability?.reliability_score ?? selectedWorker.reliability_score) })}</span>}
              {(selectedWorker.agencyNames || []).map((name) => <span key={`agency-${name}`} className="admin-filter">{name}</span>)}
              {(selectedWorker.companyNames || []).map((name) => <span key={`company-${name}`} className="admin-filter">{name}</span>)}
            </div>
            <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-form-grid">
                <div>
                  <strong>{t('Worker identity')}</strong>
                  <p className="helper">{[selectedWorker.phone || selectedWorker.workerEmail, selectedWorker.public_headline].filter(Boolean).join(' / ') || t('Contact not provided')}</p>
                </div>
                <div>
                  <strong>{t('Workforce relationship')}</strong>
                  <p className="helper">{[selectedWorker.owningOrgName || selectedWorker.primaryAgencyName || selectedWorker.primaryCompanyName, selectedWorker.relationshipStatus ? t(readable(selectedWorker.relationshipStatus)) : null].filter(Boolean).join(' / ') || t('No active relationship summary')}</p>
                </div>
                <div>
                  <strong>{t('Compliance summary')}</strong>
                  <p className="helper">{documentRisksKnown ? t('{documents} visible documents / {risks} visible risk items', { documents: (selectedWorker.documents || []).length, risks: documentRisks.filter((risk) => risk.workerProfileId === selectedWorker.worker_profile_id).length }) : t('Document risks unavailable')}</p>
                </div>
                <div>
                  <strong>{t('Availability / activity')}</strong>
                  <p className="helper">{t('{windows} availability windows / {events} attendance events', { windows: selectedAvailability?.recurringAvailability.length || 0, events: selectedAttendanceEvents.length })}</p>
                </div>
              </div>
            </div>
            <div className="admin-tabs" role="tablist" aria-label={t('Worker detail sections')}>
              {workerDetailTabs.map((tab) => (
                <button key={tab.id} type="button" className={`admin-tab ${activeWorkerDetailTab === tab.id ? 'active' : ''}`} onClick={() => setActiveWorkerDetailTab(tab.id)}>
                  {t(tab.label)}
                </button>
              ))}
            </div>
            {activeWorkerDetailTab === 'overview' ? (
            <div className="admin-form-grid">
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Contact info')}</h4>
                <p className="helper">{selectedWorker.phone || selectedWorker.workerEmail || t('No contact info provided.')}</p>
                <p className="helper">{objectSummary(selectedWorker.address)}</p>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Mailing/profile address')}</h4>
                <p className="helper">{[
                  selectedWorker.addressLine1,
                  selectedWorker.addressLine2,
                  selectedWorker.city,
                  selectedWorker.region,
                  selectedWorker.postalCode,
                  selectedWorker.country,
                ].filter(Boolean).join(', ') || t('Not provided')}</p>
                <p className="helper">{t('This address is not used for geographic job matching.')}</p>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Work search area')}</h4>
                <p className="helper">
                  {selectedWorker.workSearchLat != null && selectedWorker.workSearchLon != null
                    ? `${Number(selectedWorker.workSearchLat).toFixed(5)}, ${Number(selectedWorker.workSearchLon).toFixed(5)}`
                    : t('No preferred job location saved.')}
                </p>
                <p className="helper">{t('{km} km radius / {consent}', {
                  km: Number(selectedWorker.workSearchRadiusMeters ?? 25000) / 1000,
                  consent: selectedWorker.locationConsent ? t('location matching consent enabled') : t('location matching consent off'),
                })}</p>
                <p className="helper">{t('Source: {source} / updated {updated}', {
                  source: selectedWorker.workSearchLocationSource || t('manual'),
                  updated: selectedWorker.workSearchLocationUpdatedAt
                    ? new Date(selectedWorker.workSearchLocationUpdatedAt).toLocaleString()
                    : t('not recorded'),
                })}</p>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Latest trusted GPS')}</h4>
                {selectedWorker.latestTrustedLocation ? (
                  <>
                    <p className="helper">{`${selectedWorker.latestTrustedLocation.lat.toFixed(5)}, ${selectedWorker.latestTrustedLocation.lon.toFixed(5)}`}</p>
                    <p className="helper">{t('Source: {source} / captured {captured}', {
                      source: selectedWorker.latestTrustedLocation.source || t('location event'),
                      captured: selectedWorker.latestTrustedLocation.capturedAt
                        ? new Date(selectedWorker.latestTrustedLocation.capturedAt).toLocaleString()
                        : t('not recorded'),
                    })}</p>
                    {selectedWorker.latestTrustedLocation.accuracyMeters != null
                      ? <p className="helper">{t('Recorded accuracy: {meters} m', { meters: Math.round(selectedWorker.latestTrustedLocation.accuracyMeters) })}</p>
                      : null}
                  </>
                ) : <p className="helper">{t('No trusted GPS event recorded.')}</p>}
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Reliability')}</h4>
                {selectedReliabilityError ? <p className="admin-disabled-callout">{selectedReliabilityError}</p> : selectedReliability ? (
                  <>
                    <p className="helper">{t('Reliability {score}/100', { score: scoreLabel(selectedReliability.reliabilityScore ?? selectedReliability.reliability_score) })}</p>
                    <p className="helper">{t('Attendance {attendance}% / Punctuality {punctuality}% / Completion {completion}%', { attendance: scoreLabel(selectedReliability.attendancePercentage ?? selectedReliability.attendance_score ?? selectedWorker.attendance_score), punctuality: scoreLabel(selectedReliability.punctualityPercentage ?? selectedReliability.punctuality_score ?? selectedWorker.punctuality_score), completion: scoreLabel(selectedReliability.completionPercentage ?? selectedReliability.completion_score ?? selectedWorker.completion_score) })}</p>
                    <p className="helper">{t('Break compliance {compliance}% / Review score {review}/5', { compliance: scoreLabel(selectedReliability.break_compliance_score ?? selectedWorker.break_compliance_score), review: selectedReliability.supervisorRating || selectedReliability.supervisor_rating || selectedWorker.supervisor_rating ? Number(selectedReliability.supervisorRating ?? selectedReliability.supervisor_rating ?? selectedWorker.supervisor_rating).toFixed(1) : '-' })}</p>
                    <p className="helper">{t('{events} attendance events / {reviews} supervisor reviews visible in scope.', { events: selectedAttendanceEvents.length, reviews: selectedReviews.length })}</p>
                  </>
                ) : <p className="helper">{t('No reliability data yet.')}</p>}
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Emergency contact')}</h4>
                <p className="helper">{objectSummary(selectedWorker.emergencyContact)}</p>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Availability')}</h4>
                {selectedAvailabilityError ? <p className="admin-disabled-callout">{selectedAvailabilityError}</p> : selectedAvailability ? (
                  <div className="stack">
                    <div>
                      <strong>{t('Recurring windows')}</strong>
                      <p className="helper">{selectedAvailability.recurringAvailability.length ? selectedAvailability.recurringAvailability.map((window) => availabilityWindowLabel(window, t)).join(' | ') : t('No recurring availability declared.')}</p>
                    </div>
                    {hasAvailabilitySummary(selectedAvailability.profileAvailability) ? (
                      <div>
                        <strong>{t('Profile availability summary')}</strong>
                        <p className="helper">{availabilitySummary(selectedAvailability.profileAvailability)}</p>
                      </div>
                    ) : null}
                    <div>
                      <strong>{t('Unavailable / blackout')}</strong>
                      <p className="helper">{selectedAvailability.unavailabilityWindows.length ? selectedAvailability.unavailabilityWindows.map((window) => `${formatWindowTime(window.starts_at, window.ends_at)}${window.reason ? ` - ${window.reason}` : ''}`).join(' | ') : t('No blackout periods declared.')}</p>
                    </div>
                    <div>
                      <strong>{t('Upcoming conflicts')}</strong>
                      <p className="helper">{selectedAvailability.upcomingConflicts.length ? selectedAvailability.upcomingConflicts.map((conflict) => conflict.message || t(readable(conflict.conflict_type))).join(' | ') : t('No upcoming availability conflicts.')}</p>
                    </div>
                  </div>
                ) : <p className="helper">{availabilitySummary(selectedWorker.availability)}</p>}
              </div>
              {selectedWorker.preferredLocations?.length ? (
                <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                  <h4>{t('Preferred locations')}</h4>
                  <p className="helper">{selectedWorker.preferredLocations.map((location) => [location.locationName, location.locationAddress].filter(Boolean).join(' - ')).join(', ')}</p>
                  <p className="helper">{t('Named worker preferences only; onsite matching uses the work search area above.')}</p>
                </div>
              ) : null}
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Languages')}</h4>
                <p className="helper">{friendlyList(selectedWorker.languages)}</p>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <h4>{t('Work eligibility')}</h4>
                <p className="helper">{objectSummary(selectedWorker.workEligibility)}</p>
              </div>
            </div>
            ) : null}
            {activeWorkerDetailTab === 'employment' ? (
            <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
              <h4>{t('Employment & Access')}</h4>
              <div className="admin-detail-grid">
                <span>{t('Worker')}</span><strong>{workerName(selectedWorker)}</strong>
                <span>{t('Organization relationship')}</span><strong>{[selectedWorker.owningOrgName || selectedWorker.primaryAgencyName || selectedWorker.primaryCompanyName, selectedWorker.relationshipStatus ? t(readable(selectedWorker.relationshipStatus)) : null].filter(Boolean).join(' / ') || '-'}</strong>
                <span>{t('Supplier / agency')}</span><strong>{selectedWorker.supplyingOrgName || selectedWorker.primaryAgencyName || '-'}</strong>
                <span>{t('Client / company')}</span><strong>{selectedWorker.clientOrgName || selectedWorker.primaryCompanyName || '-'}</strong>
                <span>{t('Account access')}</span><strong>{selectedWorker.worker_user_id ? t('App access active') : t('No login account')}</strong>
                <span>{t('Worker profile')}</span><strong>{t('Internal ID kept for API actions only')}</strong>
              </div>
            </div>
            ) : null}
            {activeWorkerDetailTab === 'skills' ? (
            <>
            <div>
              <h4>{t('Skills')}</h4>
              <div className="admin-chip-list">
                {selectedWorkerCanonicalSkills.map((skill) => (
                  <span key={skill.skill_id} className="admin-chip">
                    {skill.canonical_name}
                    <small>{t(workerSkillLabel(skill.verification_status, skill.source))}</small>
                  </span>
                ))}
                {selectedWorkerCanonicalSkills.length === 0 ? <span className="helper">{t('No skills recorded yet.')}</span> : null}
              </div>
            </div>
            <div>
              <h4>{t('Learning intelligence')}</h4>
              {selectedWorker.learningProfile ? (
                <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                  <div className="admin-form-grid">
                    <div>
                      <strong>{t('Momentum')}</strong>
                      <p className="helper">{t(selectedWorker.learningProfile.overallLearningMomentum)} / {t('{count} day streak', { count: selectedWorker.learningProfile.streakDays })}</p>
                    </div>
                    <div>
                      <strong>{t('Current certification path')}</strong>
                      <p className="helper">{selectedWorker.learningProfile.activeCertification?.title || t('No active certification path')}</p>
                      {selectedWorker.learningProfile.activeCertification ? <p className="helper">{t('{progress}% progress', { progress: selectedWorker.learningProfile.activeCertification.progress })}</p> : null}
                    </div>
                    <div>
                      <strong>{t('Next competency')}</strong>
                      <p className="helper">{selectedWorker.learningProfile.recommendedNextCompetency?.nextCompetency || t('No recommendation yet')}</p>
                      {selectedWorker.learningProfile.estimatedTimeToNextCertification ? <p className="helper">{selectedWorker.learningProfile.estimatedTimeToNextCertification}</p> : null}
                    </div>
                    <div>
                      <strong>{t('Learning activity')}</strong>
                      <p className="helper">{t(learningActivityRecency(selectedWorker.learningProfile.recentLearningActivity[0]?.completedAt))}</p>
                    </div>
                  </div>
                  <div className="admin-chip-list">
                    {selectedWorker.learningProfile.masteredCompetencies.slice(0, 4).map((competency) => (
                      <span key={`mastered-${competency.skillName}-${competency.name}`} className="admin-chip">
                        {competency.name}
                        <small>{t('Mastered')} / {competency.skillName}</small>
                      </span>
                    ))}
                    {selectedWorker.learningProfile.needsReview.slice(0, 4).map((competency) => (
                      <span key={`review-${competency.skillName}-${competency.name}`} className="admin-chip">
                        {competency.name}
                        <small>{t('Needs review')} / {competency.skillName}</small>
                      </span>
                    ))}
                    {selectedWorker.learningProfile.masteredCompetencies.length === 0 && selectedWorker.learningProfile.needsReview.length === 0
                      ? <span className="helper">{t('No competency summary yet.')}</span>
                      : null}
                  </div>
                  {selectedWorker.learningProfile.mentorRecommendationHints.length > 0 ? (
                    <p className="helper">{selectedWorker.learningProfile.mentorRecommendationHints.slice(0, 2).map((hint) => hint.reason).join(' | ')}</p>
                  ) : null}
                </div>
              ) : <p className="helper">{t('No learning profile summary yet.')}</p>}
            </div>
            <div>
              <h4>{t('Skill progression ladder')}</h4>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead>
                    <tr>
                      <th>{t('Skill')}</th>
                      <th>{t('Current level')}</th>
                      <th>{t('Certification')}</th>
                      <th>{t('Completed work')}</th>
                      <th>{t('Mentorship')}</th>
                      <th>{t('Evidence')}</th>
                      <th>{t('Updated')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedWorkerSkillProgression.map((skill) => (
                      <tr key={skill.skillId}>
                        <td><strong>{skill.canonicalName}</strong>{skill.category ? <div className="helper">{skill.category}</div> : null}</td>
                        <td><span className="admin-chip">{t(skillLevelLabel(skill.currentLevel))}</span></td>
                        <td>
                          <span className="status-pill">{skill.isCertified ? t('Certified') : t('Not certified')}</span>
                          {skill.certifiedLevelFloor ? <div className="helper">{t('Floor')}: {t(skillLevelLabel(skill.certifiedLevelFloor))}</div> : null}
                          {skill.certificationExpiresAt ? <div className="helper">{t('Expires')}: {formatDate(skill.certificationExpiresAt)}</div> : null}
                        </td>
                        <td>
                          <span>{t('{count} shifts', { count: Number(skill.completedShiftCount || 0) })}</span>
                          <div className="helper">{t('{hours} verified hours', { hours: Math.round(Number(skill.completedVerifiedHours || 0)) })}</div>
                        </td>
                        <td>
                          <span>{t('{minutes} completed', { minutes: Number(skill.mentorshipMinutesCompleted || 0) })}</span>
                          <div className="helper">{t('{minutes} taught', { minutes: Number(skill.mentorshipMinutesTaught || 0) })}</div>
                        </td>
                        <td>
                          <span>{scoreLabel(skill.evidenceScore)}</span>
                          <div className="helper">{t(evidenceSummaryLabel(skill.evidenceSummary))}</div>
                          <button type="button" className="btn btn-ghost" onClick={() => setSelectedEvidenceSkillId(skill.skillId)}>{t('View evidence')}</button>
                        </td>
                        <td>{formatDate(skill.lastProgressionRecalculatedAt)}</td>
                      </tr>
                    ))}
                    {selectedWorkerSkillProgression.length === 0 ? <tr><td colSpan={7}>{t('No skill progression has been calculated yet.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h4>{t('Evidence history')}</h4>
              <div className="inline-group">
                <label>{t('Skill')}
                  <select className="input" value={selectedEvidenceSkillId} onChange={(event) => setSelectedEvidenceSkillId(event.target.value)}>
                    <option value="">{t('Select a skill')}</option>
                    {selectedWorkerSkillProgression.map((skill) => <option key={skill.skillId} value={skill.skillId}>{skill.canonicalName}</option>)}
                  </select>
                </label>
              </div>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead><tr><th>{t('Type')}</th><th>{t('Status')}</th><th>{t('Source')}</th><th>{t('Occurred')}</th><th>{t('Expiry')}</th><th>{t('Score / weight')}</th><th>{t('Reason')}</th></tr></thead>
                  <tbody>
                    {evidenceTypeOrder.flatMap((type) => selectedWorkerSkillEvidence.filter((evidence) => evidence.evidenceType === type).map((evidence) => (
                      <tr key={evidence.id}>
                        <td>{t(readable(evidence.evidenceType))}</td>
                        <td><span className="status-pill">{t(readable(evidence.status))}</span></td>
                        <td>{evidenceSourceLabel(evidence) || '-'}</td>
                        <td>{formatDate(evidence.occurredAt)}</td>
                        <td>{formatDate(evidence.expiresAt)}</td>
                        <td>{[scoreLabel(evidence.scoreDelta), evidence.evidenceWeight ? `w ${scoreLabel(evidence.evidenceWeight)}` : null].filter(Boolean).join(' / ') || '-'}</td>
                        <td>{evidenceMetadataSummary(evidence.metadata) || '-'}</td>
                      </tr>
                    )))}
                    {skillEvidenceLoading ? <tr><td colSpan={7}>{t('Loading evidence...')}</td></tr> : null}
                    {!skillEvidenceLoading && selectedEvidenceSkillId && selectedWorkerSkillEvidence.length === 0 ? <tr><td colSpan={7}>{t('No evidence recorded for this skill yet.')}</td></tr> : null}
                    {!skillEvidenceLoading && !selectedEvidenceSkillId ? <tr><td colSpan={7}>{t('Select a skill to view evidence history.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h4>{t('Recommended Skills')}</h4>
              <div className="admin-chip-list">
                {selectedWorkerRecommendations.map((recommendation) => (
                  <span key={recommendation.skillId} className="admin-chip">
                    {recommendation.canonicalName}
                    <small>{t(readable(recommendation.recommendationType))}</small>
                    <button type="button" className="btn btn-ghost" disabled={!canManageWorkers || saving} onClick={() => handleAddRecommendedSkill(recommendation)}>{t('Add Skill')}</button>
                  </span>
                ))}
                {selectedWorkerRecommendations.length === 0 ? <span className="helper">{t('No recommendations yet.')}</span> : null}
              </div>
            </div>
            <div>
              <h4>{t('Skill Experience')}</h4>
              <div className="admin-chip-list">
                {selectedWorkerSkillExperience.map((experience) => (
                  <span key={experience.skillId} className="admin-chip">
                    {experience.canonicalName}
                    <small>{t('{hours} hrs / {assignments} assignments / {level}', { hours: Math.round(experience.experienceHours), assignments: experience.experienceAssignments, level: t(experience.levelLabel) })}</small>
                  </span>
                ))}
                {selectedWorkerSkillExperience.length === 0 ? <span className="helper">{t('No completed-work experience recorded yet.')}</span> : null}
              </div>
            </div>
            </>
            ) : null}
            {activeWorkerDetailTab === 'availability' ? (
            <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
              <h4>{t('Availability')}</h4>
              {selectedAvailabilityError ? <p className="admin-disabled-callout">{selectedAvailabilityError}</p> : selectedAvailability ? (
                <div className="stack">
                  <p className="helper">{selectedAvailability.recurringAvailability.length ? selectedAvailability.recurringAvailability.map((window) => availabilityWindowLabel(window, t)).join(' | ') : t('No recurring availability declared.')}</p>
                  <p className="helper">{selectedAvailability.unavailabilityWindows.length ? selectedAvailability.unavailabilityWindows.map((window) => `${formatWindowTime(window.starts_at, window.ends_at)}${window.reason ? ` - ${window.reason}` : ''}`).join(' | ') : t('No blackout periods declared.')}</p>
                  <p className="helper">{selectedAvailability.upcomingConflicts.length ? selectedAvailability.upcomingConflicts.map((conflict) => conflict.message || t(readable(conflict.conflict_type))).join(' | ') : t('No upcoming availability conflicts.')}</p>
                </div>
              ) : <p className="helper">{availabilitySummary(selectedWorker.availability)}</p>}
            </div>
            ) : null}
            {activeWorkerDetailTab === 'activity' ? (
            <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
              <h4>{t('Activity')}</h4>
              <table className="workspace-table">
                <thead><tr><th>{t('Type')}</th><th>{t('Occurred')}</th><th>{t('Severity')}</th><th>{t('Notes')}</th></tr></thead>
                <tbody>
                  {selectedAttendanceEvents.slice(0, 25).map((event) => (
                    <tr key={event.id}>
                      <td>{t(readable(event.eventType || event.event_type || 'attendance'))}</td>
                      <td>{formatDate(event.occurredAt || event.occurred_at)}</td>
                      <td>{event.severity || '-'}</td>
                      <td>{event.notes || '-'}</td>
                    </tr>
                  ))}
                  {selectedAttendanceEvents.length === 0 ? <tr><td colSpan={4}>{t('No attendance activity visible yet.')}</td></tr> : null}
                </tbody>
              </table>
            </div>
            ) : null}
            {activeWorkerDetailTab === 'compliance' ? (
            <div>
              {accessContext?.billingEntitlements?.features?.['workforce.employment_compliance']?.enabled === true && hasAnyPermission(['tenant.employment.manage', 'tenant.employment.verify']) && selectedOrgId ? (
                <EmploymentCompliancePanel organizationId={selectedOrgId} workerProfileId={selectedWorker.worker_profile_id} workerName={workerName(selectedWorker)} />
              ) : null}
              {selectedWorker.worker_profile_id === selectedWorkerProfileId ? <WorkerCredentialSummaryPanel workerProfileId={selectedWorker.worker_profile_id} /> : null}
              <h4>{industryLabels.explicitIndustry ? `${industryLabels.label('worker')}-owned documents` : t('Worker-owned documents')}</h4>
              <p className="helper">{industryLabels.explicitIndustry ? `The ${industryLabels.label('worker').toLowerCase()} uploads and owns these files. Your organization may review, verify, reject, expire, or revoke document records only within your permitted relationship and consent scope.` : t('The worker uploads and owns these files. Your organization may review, verify, reject, expire, or revoke document records only within your permitted relationship and consent scope.')}</p>
              <p className="helper">{t('Visibility: Free-agent visibility never exposes private documents or sensitive identity files.')}</p>
              <button type="button" className="btn btn-secondary" title={t('Adding or reviewing documents requires document management permission.')} disabled={!canManageOperationalDocuments || saving} onClick={() => openDocumentModal()}>{t('Add document')}</button>
              <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead><tr><th>{t('Name')}</th><th>{t('Type')}</th><th>{t('Issuer')}</th><th>{t('Dates')}</th><th>{t('Status')}</th><th>{t('Linked skills')}</th><th>{t('File')}</th><th>{t('Review')}</th><th>{t('Actions')}</th></tr></thead>
                  <tbody>
                    {(selectedWorker.documents || []).map((document) => (
                      <React.Fragment key={document.documentId}>
                        <tr>
                          <td>{document.documentName}</td>
                          <td>{t(readable(document.documentType))}{document.sensitivityClass === 'sensitive_identity' ? <span className="status-pill">{t('Sensitive')}</span> : null}</td>
                          <td>{document.issuingAuthority || '-'}</td>
                          <td>{[formatDate(document.issueDate), formatDate(document.expiryDate)].filter((date) => date && date !== '-').join(` ${t('to')} `) || '-'}</td>
                          <td><span className="status-pill">{t(readable(documentStatus(document)))}</span></td>
                          <td>
                            <div className="admin-chip-list">
                              {(document.linkedSkills || []).map((link) => <span key={link.id || link.skillId} className="admin-chip">{link.canonicalName}<small>{link.levelFloor ? t(skillLevelLabel(link.levelFloor)) : t('Default floor')}</small></span>)}
                              {(document.linkedSkills || []).length === 0 ? <span className="helper">{t('No linked skills')}</span> : null}
                            </div>
                          </td>
                          <td>{document.fileUrl ? <button type="button" className="btn btn-ghost" disabled={openingDocumentId === document.documentId} onClick={() => handleOpenDocumentFile(document)}>{t('Open')}</button> : document.originalFileName || '-'}</td>
                          <td>
                            {[document.reviewNotes, document.rejectionReason].filter(Boolean).join(' | ') || '-'}
                          </td>
                          <td className="inline-group">
                            <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => openDocumentModal(document)}>{t('Edit')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => {
                              setVerifyTarget(document)
                              setDocumentReviewForm({ reason: '', notes: document.reviewNotes || '' })
                              setDocumentSkillLinkSelection([])
                              setDocumentSkillLinkLevelFloor('proficient')
                            }}>{t('Verify')}</button>
                            {canEditDocumentSkillLinks(document) ? (
                              <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => openSkillLinksModal(document)}>{t('Edit linked skills')}</button>
                            ) : null}
                            <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => {
                              setRejectTarget(document)
                              setDocumentReviewForm({ reason: document.rejectionReason || '', notes: document.reviewNotes || '' })
                            }}>{t('Reject')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => handleExpireDocument(document)}>{t('Expire')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageOperationalDocuments || saving} onClick={() => handleRevokeDocument(document)}>{t(documentRemoveLabel(document))}</button>
                          </td>
                        </tr>
                        {document.extractionStatus && document.extractionStatus !== 'not_started' ? (
                          <tr>
                            <td colSpan={9}>
                              <div className="stack">
                                <strong>{t('Assistive OCR suggestions')}</strong>
                                <p className="helper">
                                  {[
                                    document.extractionStatus === 'user_confirmed' ? t('Worker confirmed') : t(readable(document.extractionStatus)),
                                    document.extractionSource ? t(readable(document.extractionSource)) : null,
                                    typeof document.extractionConfidence === 'number' ? `${Math.round(document.extractionConfidence * 100)}%` : null,
                                    [document.ocrEngine, document.ocrEngineVersion].filter(Boolean).join(' ')
                                  ].filter(Boolean).join(' | ')}
                                </p>
                                {document.extractedFields ? (
                                  <div className="admin-chip-row">
                                    {Object.entries(document.extractedFields).filter(([, value]) => Boolean(value)).map(([key, value]) => (
                                      <span key={key} className="admin-chip">{t(readable(key))}: <small>{String(value)}</small></span>
                                    ))}
                                  </div>
                                ) : null}
                                {document.extractedTextPreview ? <p className="helper">{document.extractedTextPreview}</p> : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                    {(selectedWorker.documents || []).length === 0 ? <tr><td colSpan={9}>{t('No certification or document metadata provided yet.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              <WorkerDocumentConsentSection
                workerProfileId={selectedWorker.worker_profile_id}
                workerName={workerName(selectedWorker)}
                organizations={organizations}
                selectedOrgId={selectedOrgId}
                canManageDocumentConsent={canManageDocumentConsent}
              />
            </div>
            ) : null}
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={t('Worker shift history')}
        description={t('Historical shifts, punches, breaks, exceptions, and GPS trails visible in your current scope.')}
        isOpen={Boolean(historyWorkerProfileId)}
        onClose={() => setHistoryWorkerProfileId('')}
        size="xl"
      >
        {historyWorkerProfileId ? <ShiftHistoryViewer workerId={historyWorkerProfileId} /> : null}
      </FormModal>

      <FormModal
        title={industryLabels.explicitIndustry ? `Verify ${industryLabels.label('worker').toLowerCase()} document?` : t('Verify worker document?')}
        description={verifyTarget ? t('{document} will be marked verified for {worker}.', { document: verifyTarget.documentName, worker: selectedWorker ? workerName(selectedWorker) : (industryLabels.explicitIndustry ? `this ${industryLabels.label('worker').toLowerCase()}` : t('this worker')) }) : undefined}
        isOpen={Boolean(verifyTarget)}
        onClose={() => {
          setVerifyTarget(null)
          setDocumentReviewForm({ reason: '', notes: '' })
          setDocumentSkillLinkSelection([])
          setDocumentSkillLinkLevelFloor('proficient')
        }}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => {
              setVerifyTarget(null)
              setDocumentReviewForm({ reason: '', notes: '' })
              setDocumentSkillLinkSelection([])
              setDocumentSkillLinkLevelFloor('proficient')
            }}>{t('Cancel')}</button>
            <button type="submit" form="worker-document-verify-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Verifying...') : t('Verify')}</button>
          </>
        )}
      >
        <form id="worker-document-verify-form" className="stack" onSubmit={handleVerifyDocument}>
          <p className="helper">{t('Linked verified certificates can raise a worker skill level, but they do not bypass backend eligibility rules.')}</p>
          {(verifyTarget?.linkedSkills || []).length ? (
            <div className="admin-chip-list">
              {verifyTarget?.linkedSkills?.map((link) => <span key={link.id || link.skillId} className="admin-chip">{link.canonicalName}<small>{link.levelFloor ? t(skillLevelLabel(link.levelFloor)) : t('Default floor')}</small></span>)}
            </div>
          ) : <p className="helper">{t('No skills are currently linked to this document.')}</p>}
          <SkillPicker
            selectedSkills={documentSkillLinkSelection}
            onChange={setDocumentSkillLinkSelection}
            label={t('Link certificate to skills')}
            placeholder={t('Search canonical skills')}
            requestContext="worker_document_skill_links"
            disabled={!canManageDocumentConsent || saving}
          />
          <div className="form-row">
            <label htmlFor="document-skill-level-floor">{t('Certificate level floor')}</label>
            <select id="document-skill-level-floor" className="input" value={documentSkillLinkLevelFloor} onChange={(event) => setDocumentSkillLinkLevelFloor(event.target.value)} disabled={!canManageDocumentConsent || saving}>
              {skillLevels.map((level) => <option key={level} value={level}>{t(skillLevelLabel(level))}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="document-verify-notes">{t('Reviewer notes')}</label>
            <textarea id="document-verify-notes" className="input" rows={3} value={documentReviewForm.notes} onChange={(event) => setDocumentReviewForm({ ...documentReviewForm, notes: event.target.value })} />
          </div>
        </form>
      </FormModal>

      <FormModal
        title={t('Edit linked skills')}
        description={skillLinksTarget ? skillLinksTarget.documentName : undefined}
        isOpen={Boolean(skillLinksTarget)}
        onClose={() => {
          setSkillLinksTarget(null)
          setDocumentSkillLinkSelection([])
          setDocumentSkillLinkLevelFloor('proficient')
        }}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => {
              setSkillLinksTarget(null)
              setDocumentSkillLinkSelection([])
              setDocumentSkillLinkLevelFloor('proficient')
            }}>{t('Cancel')}</button>
            <button type="submit" form="worker-document-skill-links-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Saving...') : t('Save linked skills')}</button>
          </>
        )}
      >
        <form id="worker-document-skill-links-form" className="stack" onSubmit={handleSaveDocumentSkillLinks}>
          <SkillPicker
            selectedSkills={documentSkillLinkSelection}
            onChange={setDocumentSkillLinkSelection}
            label={t('Linked skills')}
            placeholder={t('Search canonical skills')}
            requestContext="worker_document_skill_links"
            disabled={!canManageDocumentConsent || saving}
          />
          <div className="form-row">
            <label htmlFor="document-edit-skill-level-floor">{t('Certificate level floor')}</label>
            <select id="document-edit-skill-level-floor" className="input" value={documentSkillLinkLevelFloor} onChange={(event) => setDocumentSkillLinkLevelFloor(event.target.value)} disabled={!canManageDocumentConsent || saving}>
              {skillLevels.filter((level) => ['proficient', 'expert', 'master'].includes(level)).map((level) => <option key={level} value={level}>{t(skillLevelLabel(level))}</option>)}
            </select>
          </div>
        </form>
      </FormModal>

      <FormModal
        title={documentEditTarget ? (industryLabels.explicitIndustry ? `Edit ${industryLabels.label('worker').toLowerCase()} document` : t('Edit worker document')) : (industryLabels.explicitIndustry ? `Add ${industryLabels.label('worker').toLowerCase()} document` : t('Add worker document'))}
        description={selectedWorker ? workerName(selectedWorker) : undefined}
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setShowDocumentModal(false)}>{t('Cancel')}</button>
            <button type="submit" form="worker-document-edit-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Saving...') : t('Save document')}</button>
          </>
        )}
      >
        <form id="worker-document-edit-form" className="admin-form-grid" onSubmit={handleSaveDocument}>
          <div className="form-row">
            <label htmlFor="document-name">{t('Name')}</label>
            <input id="document-name" className="input" value={documentForm.documentName} onChange={(event) => setDocumentForm({ ...documentForm, documentName: event.target.value })} required />
          </div>
          <div className="form-row">
            <label htmlFor="document-type">{t('Type')}</label>
            <select id="document-type" className="input" value={documentForm.documentType} onChange={(event) => setDocumentForm({ ...documentForm, documentType: event.target.value })} required>
              <optgroup label={t('Profile media')}>
                {workerDocumentTypeOptions.filter((type) => type === 'profile_photo').map((type) => <option key={type} value={type}>{t(readable(type))}</option>)}
              </optgroup>
              <optgroup label={t('Compliance documents')}>
                {workerDocumentTypeOptions.filter((type) => type !== 'profile_photo').map((type) => <option key={type} value={type}>{t(readable(type))}</option>)}
              </optgroup>
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="document-issuer">{t('Issuer')}</label>
            <input id="document-issuer" className="input" value={documentForm.issuingAuthority} onChange={(event) => setDocumentForm({ ...documentForm, issuingAuthority: event.target.value })} />
          </div>
          <div className="form-row">
            <label htmlFor="document-status">{t('Status')}</label>
            <select id="document-status" className="input" value={documentForm.status} onChange={(event) => setDocumentForm({ ...documentForm, status: event.target.value })}>
              <option value="draft">{t('Draft')}</option>
              <option value="submitted">{t('Submitted')}</option>
              <option value="verified">{t('Verified')}</option>
              <option value="rejected">{t('Rejected')}</option>
              <option value="expired">{t('Expired')}</option>
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="document-issue-date">{t('Issue date')}</label>
            <input id="document-issue-date" className="input" type="date" value={documentForm.issueDate} onChange={(event) => setDocumentForm({ ...documentForm, issueDate: event.target.value })} />
          </div>
          <div className="form-row">
            <label htmlFor="document-expiry-date">{t('Expiry date')}</label>
            <input id="document-expiry-date" className="input" type="date" value={documentForm.expiryDate} onChange={(event) => setDocumentForm({ ...documentForm, expiryDate: event.target.value })} />
          </div>
          <div className="form-row admin-form-wide">
            <label htmlFor="document-notes">{t('Notes')}</label>
            <textarea id="document-notes" className="input" rows={3} value={documentForm.notes} onChange={(event) => setDocumentForm({ ...documentForm, notes: event.target.value })} />
          </div>
        </form>
      </FormModal>

      <FormModal
        title={industryLabels.explicitIndustry ? `Reject ${industryLabels.label('worker').toLowerCase()} document` : t('Reject worker document')}
        description={rejectTarget ? rejectTarget.documentName : undefined}
        isOpen={Boolean(rejectTarget)}
        onClose={() => {
          setRejectTarget(null)
          setDocumentReviewForm({ reason: '', notes: '' })
        }}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => {
              setRejectTarget(null)
              setDocumentReviewForm({ reason: '', notes: '' })
            }}>{t('Cancel')}</button>
            <button type="submit" form="worker-document-reject-form" className="btn btn-primary" disabled={!canManageDocumentConsent || saving}>{saving ? t('Rejecting...') : t('Reject document')}</button>
          </>
        )}
      >
        <form id="worker-document-reject-form" className="stack" onSubmit={handleRejectDocument}>
          <div className="form-row">
            <label htmlFor="document-reject-reason">{t('Reason')}</label>
            <textarea id="document-reject-reason" className="input" rows={3} value={documentReviewForm.reason} onChange={(event) => setDocumentReviewForm({ ...documentReviewForm, reason: event.target.value })} />
          </div>
          <div className="form-row">
            <label htmlFor="document-review-notes">{t('Reviewer notes')}</label>
            <textarea id="document-review-notes" className="input" rows={3} value={documentReviewForm.notes} onChange={(event) => setDocumentReviewForm({ ...documentReviewForm, notes: event.target.value })} />
          </div>
        </form>
      </FormModal>

      <FormModal
        title={industryLabels.explicitIndustry ? `Edit ${industryLabels.label('worker').toLowerCase()} profile` : t('Edit worker profile')}
        description={industryLabels.explicitIndustry ? `Update basic profile fields without expanding the ${industryLabels.label('worker').toLowerCase()} list.` : t('Update basic profile fields without expanding the worker list.')}
        isOpen={showProfileModal && Boolean(selectedWorker)}
        onClose={() => setShowProfileModal(false)}
        size="lg"
      >
        {selectedWorker ? (
          <form className="stack" onSubmit={handleProfileUpdate}>
            {!canEditSelectedGlobalProfile ? <p className="helper">{t('Global profile and visibility settings are managed by the account owner or system security administration.')}</p> : null}
            <div className="admin-form-grid">
              <div className="form-row"><label htmlFor="profile-first-name">{t('First name')}</label><input id="profile-first-name" className="input" value={profileDraft.firstName} onChange={(event) => setProfileDraft({ ...profileDraft, firstName: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-last-name">{t('Last name')}</label><input id="profile-last-name" className="input" value={profileDraft.lastName} onChange={(event) => setProfileDraft({ ...profileDraft, lastName: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-phone">{t('Phone')}</label><input id="profile-phone" className="input" value={profileDraft.phone} onChange={(event) => setProfileDraft({ ...profileDraft, phone: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-headline">{t('Headline')}</label><input id="profile-headline" className="input" value={profileDraft.publicHeadline} onChange={(event) => setProfileDraft({ ...profileDraft, publicHeadline: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row admin-form-wide"><label htmlFor="profile-bio">{t('Bio')}</label><textarea id="profile-bio" className="input" value={profileDraft.bio} onChange={(event) => setProfileDraft({ ...profileDraft, bio: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <p className="helper">{t('Mailing address is used for profile, contact, and administrative purposes only.')}</p>
              <div className="form-row"><label htmlFor="profile-address-1">{t('Mailing address line 1')}</label><input id="profile-address-1" className="input" value={profileDraft.addressLine1} onChange={(event) => setProfileDraft({ ...profileDraft, addressLine1: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-address-2">{t('Mailing address line 2')}</label><input id="profile-address-2" className="input" value={profileDraft.addressLine2} onChange={(event) => setProfileDraft({ ...profileDraft, addressLine2: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-city">{t('City')}</label><input id="profile-city" className="input" value={profileDraft.city} onChange={(event) => setProfileDraft({ ...profileDraft, city: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-region">{t('Province/State/Region')}</label><input id="profile-region" className="input" value={profileDraft.region} onChange={(event) => setProfileDraft({ ...profileDraft, region: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-postal">{t('Postal code')}</label><input id="profile-postal" className="input" value={profileDraft.postalCode} onChange={(event) => setProfileDraft({ ...profileDraft, postalCode: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <div className="form-row"><label htmlFor="profile-country">{t('Country')}</label><input id="profile-country" className="input" value={profileDraft.country} onChange={(event) => setProfileDraft({ ...profileDraft, country: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <p className="helper">{t('Work search area and radius control nearby onsite and hybrid matching; mailing address does not.')}</p>
              <div className="form-row"><label htmlFor="profile-radius">{t('Work search radius km')}</label><input id="profile-radius" type="number" min={1} max={250} className="input" value={profileDraft.workRadiusKm} onChange={(event) => setProfileDraft({ ...profileDraft, workRadiusKm: event.target.value })} disabled={!canEditSelectedGlobalProfile} /></div>
              <label className="checkbox-row admin-form-wide"><input type="checkbox" checked={profileDraft.locationConsent} onChange={(event) => setProfileDraft({ ...profileDraft, locationConsent: event.target.checked })} disabled={!canEditSelectedGlobalProfile} /> {t('Location matching consent enabled')}</label>
            </div>
            <button type="submit" className="btn btn-primary" disabled={!canEditSelectedGlobalProfile || saving}>{saving ? t('Saving...') : t('Save profile')}</button>
          </form>
        ) : null}
      </FormModal>

      <FormModal
        title={industryLabels.explicitIndustry ? `Manage ${industryLabels.label('worker').toLowerCase()} skills` : t('Manage worker skills')}
        description={industryLabels.explicitIndustry ? `Add or remove skills from the selected ${industryLabels.label('worker').toLowerCase()} profile.` : t('Add or remove skills from the selected worker profile.')}
        isOpen={showSkillsModal && Boolean(selectedWorker)}
        onClose={() => setShowSkillsModal(false)}
        size="md"
      >
        {selectedWorker ? (
          <div className="stack">
            <div className="inline-group">
              {selectedWorkerCanonicalSkills.map((skill) => (
                <span key={skill.skill_id} className="admin-filter active">
                  {skill.canonical_name}
                  <small>{t(workerSkillLabel(skill.verification_status, skill.source))}</small>
                  {canManageWorkers ? <button type="button" className="btn btn-ghost" onClick={() => handleRemoveSkill(skill.skill_id)} disabled={saving}>{t('Remove')}</button> : null}
                </span>
              ))}
              {selectedWorkerCanonicalSkills.length === 0 ? <span className="helper">{t('No skills recorded yet.')}</span> : null}
            </div>
            <SkillPicker
              selectedSkills={selectedWorkerCanonicalSkills.map(workerSkillToCatalogResult)}
              onChange={handleCanonicalSkillsChange}
              label={t('Add skill')}
              placeholder={t('Search canonical skills')}
              requestContext={`admin_worker:${selectedWorker.worker_profile_id}`}
              disabled={!canManageWorkers || saving}
            />
          </div>
        ) : null}
      </FormModal>

      {activeSurface === 'relationships' ? (
        <>
          <div className="admin-panel-toolbar">
            <div>
              <h3 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? `${industryLabels.label('worker')} relationships` : t('Worker relationships')}</h3>
              <p className="helper">{t('Relationship edits are scoped to {org}. These workers are not counted as Staff unless the backend classifies them as direct ownership relationships.', { org: selectedOrg?.name || t('the selected organization') })}</p>
            </div>
            <button type="button" className="btn btn-primary" disabled={!relationshipWriteAllowed} onClick={() => setShowRelationshipModal(true)}>
              {t('Create relationship')}
            </button>
          </div>
          <div className="inline-group" aria-label={t('Relationship filters')}>
            {workerFilters.filter((item) => item.id !== 'free-agents').map((item) => (
              <button key={item.id} type="button" className={`admin-filter ${filter === item.id ? 'active' : ''}`} onClick={() => setFilter(item.id)}>
                {industryLabels.explicitIndustry ? t(item.label.replace(/Workers/g, industryLabels.label('workers'))) : t(item.label)}
              </button>
            ))}
          </div>

          <FormModal
            title={industryLabels.explicitIndustry ? `Create ${industryLabels.label('worker').toLowerCase()} relationship` : t('Create worker relationship')}
            description={industryLabels.explicitIndustry ? `Assign visible ${industryLabels.label('worker').toLowerCase()} to the selected organization through a scoped relationship.` : t('Assign a visible worker to the selected organization through a scoped relationship.')}
            isOpen={showRelationshipModal}
            onClose={() => setShowRelationshipModal(false)}
            size="lg"
          >
            <form className="stack" onSubmit={handleCreate}>
              {!relationshipWriteAllowed ? <p className="helper">{t('Worker relationship creation requires worker management, worker sharing, or sharing management permission depending on relationship type.')}</p> : null}
              <div className="admin-form-grid">
                <div className="form-row">
                  <label htmlFor="worker-profile-id">{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</label>
                  <select id="worker-profile-id" className="input" value={form.workerProfileId || ''} onChange={(event) => setForm({ ...form, workerProfileId: event.target.value, workerUserId: undefined })} disabled={!canManageWorkers && !canShareWorkers}>
                    <option value="">{industryLabels.explicitIndustry ? `Choose ${industryLabels.label('worker').toLowerCase()}` : t('Choose a worker')}</option>
                    {workerProfiles.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{workerName(worker)}{worker.workerEmail ? ` - ${worker.workerEmail}` : ''}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="worker-relationship-type">{t('Relationship type')}</label>
                  <select id="worker-relationship-type" className="input" value={form.relationshipType} onChange={(event) => setForm({ ...form, relationshipType: event.target.value as WorkerRelationshipType })} disabled={!canManageWorkers && !canShareWorkers}>
                    {relationshipTypes.map((type) => <option key={type} value={type}>{t(readable(type))}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="worker-relationship-status">{t('Status')}</label>
                  <select id="worker-relationship-status" className="input" value={form.status || 'active'} onChange={(event) => setForm({ ...form, status: event.target.value as WorkerRelationshipStatus })} disabled={!canManageWorkers && !canShareWorkers}>
                    {relationshipStatuses.map((status) => <option key={status} value={status}>{t(readable(status))}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="worker-exclusive-until">{t('Exclusive until')}</label>
                  <input id="worker-exclusive-until" type="datetime-local" className="input" value={exclusiveUntilDraft} onChange={(event) => setExclusiveUntilDraft(event.target.value)} disabled={form.relationshipType !== 'exclusive_worker' || (!canManageWorkers && !canShareWorkers)} />
                </div>
                <div className="form-row">
                  <label htmlFor="worker-selected-org">{t('Organization')}</label>
                  <input id="worker-selected-org" className="input" value={selectedOrg?.name || ''} disabled />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={!relationshipWriteAllowed || saving}>{saving ? t('Saving...') : t('Create relationship')}</button>
            </form>
          </FormModal>

          <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
            {loading ? <LoadingState label={t('Loading worker relationships...')} /> : (
              <table className="workspace-table">
                <thead><tr><th>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</th><th>{t('Type')}</th><th>{t('Status')}</th><th>{t('Exclusive until')}</th><th>{t('Public info')}</th><th>{t('Actions')}</th></tr></thead>
                <tbody>
                  {visibleRelationships.map((relationship) => {
                    const draftedType = typeDrafts[relationship.id] || (relationship.relationship_type as WorkerRelationshipType)
                    const canUpdate = canManageWorkers || (canShareWorkers && ['shared_worker', 'exclusive_worker'].includes(draftedType))
                    return (
                      <tr key={relationship.id}>
                        <td>
                          <strong>{workerName(relationship)}</strong>
                          <div className="helper">{relationship.worker_email || relationship.worker_phone || t('No private contact shown')}</div>
                        </td>
                        <td>
                          <select className="input admin-inline-select" value={draftedType} onChange={(event) => setTypeDrafts({ ...typeDrafts, [relationship.id]: event.target.value as WorkerRelationshipType })} disabled={!canUpdate}>
                            {relationshipTypes.map((type) => <option key={type} value={type}>{t(readable(type))}</option>)}
                          </select>
                        </td>
                        <td>
                          <select className="input admin-inline-select" value={statusDrafts[relationship.id] || relationship.status} onChange={(event) => setStatusDrafts({ ...statusDrafts, [relationship.id]: event.target.value as WorkerRelationshipStatus })} disabled={!canUpdate}>
                            {relationshipStatuses.map((status) => <option key={status} value={status}>{t(readable(status))}</option>)}
                          </select>
                        </td>
                        <td>{formatDate(relationship.exclusive_until)}</td>
                        <td>{relationship.public_headline || relationship.bio || (relationship.is_free_agent ? t('Free agent limited profile') : '-')}</td>
                        <td className="inline-group">
                          <button type="button" className="btn btn-ghost" disabled={!canUpdate || saving} onClick={() => handleRelationshipUpdate(relationship)}>{t('Update')}</button>
                          <button type="button" className="btn btn-ghost" disabled={!canManageWorkers || saving} onClick={() => handleEndRelationship(relationship)}>{t('End')}</button>
                        </td>
                      </tr>
                    )
                  })}
                  {visibleRelationships.length === 0 ? <tr><td colSpan={6}>{industryLabels.explicitIndustry ? `No ${industryLabels.label('worker').toLowerCase()} relationships match this filter.` : t('No worker relationships match this filter.')}</td></tr> : null}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {activeSurface === 'marketplace' ? (
        <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
          <h3 style={{ marginTop: 0 }}>{t('Network-visible workers')}</h3>
          <p className="helper">{t('Free-agent profiles expose limited public fields only until a relationship and any required document consent exists.')}</p>
          {loading ? <p className="text-muted">{t('Loading free agents...')}</p> : (
            <table className="workspace-table">
              <thead><tr><th>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</th><th>{t('Headline')}</th><th>{t('Rating')}</th><th>{t('Visibility')}</th><th>{t('Relationship')}</th></tr></thead>
              <tbody>
                {freeAgents.map((worker) => (
                  <tr key={worker.worker_profile_id}>
                    <td>{workerName(worker)}</td>
                    <td>{worker.public_headline || '-'}</td>
                    <td>{worker.rating_average || '-'} {worker.rating_count ? `(${worker.rating_count})` : ''}</td>
                    <td>{worker.marketplace_visibility}</td>
                    <td>{worker.has_visible_relationship ? t('Visible relationship exists') : t('Limited marketplace profile')}</td>
                  </tr>
                ))}
                {freeAgents.length === 0 ? <tr><td colSpan={5}>{industryLabels.explicitIndustry ? `No visible free-agent ${industryLabels.label('workers').toLowerCase()} found.` : t('No visible free-agent workers found.')}</td></tr> : null}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {/* ── Worker invite codes ─────────────────────────────────────── */}
      {activeSurface === 'invites' ? (
      <>
      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? `${industryLabels.label('worker')} invite codes` : t('Worker invite codes')}</h3>
          <p className="helper">{industryLabels.explicitIndustry ? `Generate a short code that ${industryLabels.label('worker').toLowerCase()} enters during Android registration or from the link-org screen to join this organization automatically.` : t('Generate a short code that a worker enters during Android registration or from the link-org screen to join this organization automatically.')}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canManageWorkers || !selectedOrgId}
          onClick={() => { setCreatedInvite(null); setShowInviteModal(true) }}
        >
          {industryLabels.explicitIndustry ? `Invite ${industryLabels.label('worker').toLowerCase()}` : t('Invite worker')}
        </button>
      </div>

      {/* Create invite modal */}
      <FormModal
        title={industryLabels.explicitIndustry ? `Create ${industryLabels.label('worker').toLowerCase()} invite code` : t('Create worker invite code')}
        description={selectedOrg ? (industryLabels.explicitIndustry ? `Invite ${industryLabels.label('worker').toLowerCase()} to join ${selectedOrg.name}` : t('Invite a worker to join {org}', { org: selectedOrg.name })) : (industryLabels.explicitIndustry ? `Create ${industryLabels.label('worker').toLowerCase()} invite code` : t('Create a worker invite code'))}
        isOpen={showInviteModal}
        onClose={() => { setShowInviteModal(false); setCreatedInvite(null) }}
        size="md"
        footer={createdInvite ? (
          <button type="button" className="btn btn-primary" onClick={() => { setShowInviteModal(false); setCreatedInvite(null) }}>{t('Done')}</button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" disabled={inviteLoading} onClick={() => setShowInviteModal(false)}>{t('Cancel')}</button>
            <button type="submit" form="worker-invite-form" className="btn btn-primary" disabled={!canManageWorkers || !selectedOrgId || inviteLoading}>
              {inviteLoading ? t('Creating…') : t('Create invite code')}
            </button>
          </>
        )}
      >
        {createdInvite ? (
          <div className="stack">
            <p className="helper">{t('Share this code with the worker. It will only be shown once.')}</p>
            <div style={{ textAlign: 'center', padding: '1.5rem', background: 'var(--surface-muted)', borderRadius: 8 }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '0.25em', fontFamily: 'monospace', color: 'var(--link)' }}>
                {createdInvite.code}
              </div>
              <div className="helper" style={{ marginTop: '0.5rem' }}>
                {t('Expires {date} · {type} · {org}', { date: new Date(createdInvite.expiresAt).toLocaleDateString(), type: t(readable(createdInvite.relationshipType)), org: createdInvite.organizationName })}
              </div>
            </div>
            <div className="inline-group">
              <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard(createdInvite.code!, t('Invite code copied!'))}>
                {t('Copy code')}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => copyToClipboard(
                  t('Register with the Punch app and enter invite code: {code}\nOrganization: {org}\nExpires: {date}', { code: createdInvite.code!, org: createdInvite.organizationName, date: new Date(createdInvite.expiresAt).toLocaleDateString() }),
                  t('Instructions copied!')
                )}
              >
                {t('Copy instructions')}
              </button>
            </div>
          </div>
        ) : (
          <form id="worker-invite-form" className="stack" onSubmit={handleCreateInvite}>
            <div className="admin-form-grid">
              <div className="form-row">
                <label htmlFor="invite-org">{t('Organization')}</label>
                <input id="invite-org" className="input" value={selectedOrg?.name || ''} disabled />
              </div>
              <div className="form-row">
                <label htmlFor="invite-relationship-type">{t('Relationship type')}</label>
                <select id="invite-relationship-type" className="input" value={inviteForm.relationshipType} onChange={(e) => setInviteForm({ ...inviteForm, relationshipType: e.target.value })}>
                  {relationshipTypes.map((type) => <option key={type} value={type}>{t(readable(type))}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="invite-email">{t('Worker email')} <span className="helper">({t('optional')})</span></label>
                <input id="invite-email" type="email" className="input" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder={t('worker@example.com')} />
              </div>
              <div className="form-row">
                <label htmlFor="invite-expiry-days">{t('Expires after (days)')}</label>
                <input id="invite-expiry-days" type="number" min={1} max={365} className="input" value={inviteForm.expiryDays} onChange={(e) => setInviteForm({ ...inviteForm, expiryDays: e.target.value })} />
              </div>
            </div>
          </form>
        )}
      </FormModal>

      {/* Pending invites table */}
      {canManageWorkers ? (
        <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
          <table className="workspace-table">
            <thead>
              <tr>
                <th>{t('Organization')}</th>
                <th>{t('Relationship type')}</th>
                <th>{t('Status')}</th>
                <th>{t('Expires')}</th>
                <th>{t('Created')}</th>
                <th>{t('Created by')}</th>
                <th>{t('Uses')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {inviteCodes.map((invite) => (
                <tr key={invite.id}>
                  <td>{invite.organizationName}</td>
                  <td>{t(readable(invite.relationshipType))}</td>
                  <td><span className="status-pill">{t(readable(invite.status))}</span></td>
                  <td>{formatDate(invite.expiresAt)}</td>
                  <td>{formatDate(invite.createdAt)}</td>
                  <td>{invite.createdByEmail || '-'}</td>
                  <td>{invite.maxUses != null ? `${invite.useCount ?? 0}/${invite.maxUses}` : invite.useCount ?? 0}</td>
                  <td className="inline-group">
                    {invite.code ? (
                      <button type="button" className="btn btn-ghost" onClick={() => copyToClipboard(invite.code!, t('Invite code copied!'))}>{t('Copy code')}</button>
                    ) : null}
                    {invite.status === 'active' ? (
                      <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => handleRevokeInvite(invite.id)}>{t('Revoke')}</button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {inviteCodes.length === 0 ? (
                <tr><td colSpan={8}><EmptyState title={t('No invite codes yet')} description={t('Create an invite code to let workers join this organization during Android registration.')} /></td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{t('Worker visibility')}</h3>
          <p className="helper">{t('Visibility/free-agent settings are managed in a modal and enforced again by backend permissions.')}</p>
        </div>
        <button type="button" className="btn btn-secondary" disabled={!ownedWorkerProfiles.length && !canEditSelectedGlobalProfile} onClick={() => setShowVisibilityModal(true)}>
          {t('Manage visibility')}
        </button>
      </div>

      <FormModal
        title={t('Worker visibility')}
        description={t('Control marketplace visibility for the selected worker profile.')}
        isOpen={showVisibilityModal}
        onClose={() => setShowVisibilityModal(false)}
        size="lg"
      >
        <form className="stack" onSubmit={handleVisibilityUpdate}>
          <div className="admin-form-grid">
            <div className="form-row">
              <label htmlFor="visibility-worker-profile">{t('Worker')}</label>
              <select id="visibility-worker-profile" className="input" value={visibilityForm.workerProfileId} onChange={(event) => {
                handleVisibilityWorkerSelect(event.target.value)
              }} disabled={!ownedWorkerProfiles.length}>
                <option value="">{t('Choose a worker')}</option>
                {ownedWorkerProfiles.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{workerName(worker)}{worker.workerEmail ? ` - ${worker.workerEmail}` : ''}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="visibility-marketplace">{t('Marketplace visibility')}</label>
              <select id="visibility-marketplace" className="input" value={visibilityForm.marketplaceVisibility} onChange={(event) => setVisibilityForm({ ...visibilityForm, marketplaceVisibility: event.target.value as WorkerProfileVisibilityPayload['marketplaceVisibility'] })} disabled={!canEditVisibility}>
                {marketplaceVisibilityOptions.map((option) => <option key={option} value={option}>{t(readable(option))}</option>)}
              </select>
            </div>
            <label className="inline-group" style={{ alignItems: 'center' }}>
              <input type="checkbox" checked={Boolean(visibilityForm.isFreeAgent)} onChange={(event) => setVisibilityForm({ ...visibilityForm, isFreeAgent: event.target.checked })} disabled={!canEditVisibility} />
              {t('Free-agent profile')}
            </label>
            <div className="form-row">
              <label htmlFor="visibility-headline">{t('Public headline')}</label>
              <input id="visibility-headline" className="input" value={visibilityForm.publicHeadline || ''} onChange={(event) => setVisibilityForm({ ...visibilityForm, publicHeadline: event.target.value })} disabled={!canEditVisibility} />
            </div>
            <div className="form-row admin-form-wide">
              <label htmlFor="visibility-bio">{t('Public bio')}</label>
              <textarea id="visibility-bio" className="input" value={visibilityForm.bio || ''} onChange={(event) => setVisibilityForm({ ...visibilityForm, bio: event.target.value })} disabled={!canEditVisibility} />
            </div>
          </div>
          {!canEditVisibility ? <p className="helper">{t('Global profile and visibility settings are managed by the account owner or system security administration.')}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={!canEditVisibility || saving}>{saving ? t('Saving...') : t('Update visibility')}</button>
        </form>
      </FormModal>
      </>
      ) : null}

      <ConfirmDialog
        title={pendingLifecycleAction ? t('{action} worker?', { action: t(workerLifecycleActionLabels[pendingLifecycleAction.action]) }) : t('Update worker?')}
        message={pendingLifecycleAction ? t('This will apply {action} to {worker}. Continue only after confirming this is the intended worker.', { action: t(workerLifecycleActionLabels[pendingLifecycleAction.action]).toLowerCase(), worker: pendingLifecycleAction.workerName }) : ''}
        isOpen={Boolean(pendingLifecycleAction)}
        confirmLabel={pendingLifecycleAction ? t(workerLifecycleActionLabels[pendingLifecycleAction.action]) : t('Confirm')}
        cancelLabel={t('Cancel')}
        destructive={pendingLifecycleAction ? ['archive', 'delete', 'anonymize', 'suspend'].includes(pendingLifecycleAction.action) : false}
        disabled={saving}
        onCancel={() => setPendingLifecycleAction(null)}
        onConfirm={() => {
          if (!pendingLifecycleAction) return
          runWorkerLifecycle(pendingLifecycleAction.workerProfileId, pendingLifecycleAction.action)
        }}
      />

    </div>
  )
}
